import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  fr: {
    translation: {
      // Navigation
      "nav.home": "Accueil",
      "nav.blog": "Blog",
      "nav.discover": "Découvrir",
      "nav.admin": "Admin",
      "nav.login": "Se connecter",
      "nav.logout": "Déconnexion",
      "nav.comparison": "Comparaison",
      "nav.howItWorks": "Comment ça marche",
      "nav.examples": "Exemples",
      "nav.whyUs": "Pourquoi nous",
      "nav.faq": "FAQ",
      "nav.questionnaire": "Questionnaire",

      // Hero Section
      "hero.title": "Ton voyage,",
      "hero.title.ai": "optimisé par l'IA",
      "hero.subtitle":
        "Découvre ton prochain itinéraire personnalisé — vols, hôtels, météo, activités, tout en un seul clic.",
      "hero.subtitle.user":
        "{{name}}, découvre ton prochain itinéraire personnalisé — vols, hôtels, météo, activités, tout en un seul clic.",
      "hero.cta": "Crée ton itinéraire",

      // How it works
      "howItWorks.title": "Comment ça marche ?",
      "howItWorks.description": "simplifie ton voyage en 4 étapes :",
      "howItWorks.step1": "Tu indiques tes envies (destination, budget, style).",
      "howItWorks.step2": "Nous comparons en temps réel vols, hébergements et activités (prix, météo, distances).",
      "howItWorks.step3":
        "Nous créons pour toi un itinéraire jour-par-jour clair, optimisé et respectueux de ton budget.",
      "howItWorks.step4":
        "Tu reçois une proposition personnalisée avec un prix détaillé et un seul lien pour tout réserver en quelques clics.",
      "howItWorks.tagline": "Moins d'onglets, plus d'aventure.",

      // Steps details
      "step1.title": "Vos envies",
      "step1.description": "Partage-nous tes envies de voyage pour un itinéraire 100% personnalisé.",
      "step1.dest": "Indique ta destination :",
      "step1.dest.desc":
        "que ce soit Lisbonne, Tokyo ou juste l'aéroport de départ, pour que Travliaq trouve les meilleures options.",
      "step1.dates": "Précise tes dates :",
      "step1.dates.desc": "fixes ou flexibles, pour optimiser prix et météo, et te garantir un timing parfait.",
      "step1.budget": "Partage ton budget et ton style de voyage :",
      "step1.budget.desc": "solo, sac à dos, confort ou premium, on adapte chaque étape à ton rythme et à tes envies.",

      "step2.title": "Recherche intelligente",
      "step2.description": "Notre IA analyse des milliers d'options pour trouver les meilleures combinaisons.",
      "step2.scan": "On scanne les meilleures options :",
      "step2.scan.desc": "vols, hébergements et activités, via des sources fiables et mises à jour en temps réel.",
      "step2.cross": "On croise prix, météo et logistique :",
      "step2.cross.desc": "pour que chaque étape s'enchaîne naturellement, sans perte de temps ni de budget.",
      "step2.filter": "On filtre selon ton profil :",
      "step2.filter.desc": "solo, backpacker, confort ou premium, chaque résultat est ajusté à tes priorités.",

      "step3.title": "Itinéraire optimisé",
      "step3.description": "Reçois un programme détaillé jour par jour, parfaitement adapté à tes préférences.",
      "step3.program": "Programme jour par jour :",
      "step3.program.desc":
        "activités, visites, pauses et repas organisés dans un ordre logique, pour profiter sans te presser.",
      "step3.budget": "Budget maîtrisé :",
      "step3.budget.desc": "chaque étape est chiffrée pour éviter les mauvaises surprises, du vol au café du coin.",
      "step3.tips": "Astuces locales intégrées :",
      "step3.tips.desc": "spots photo, restaurants cachés, transports malins... comme si un ami sur place te guidait.",

      "step4.title": "Voyage prêt à réserver",
      "step4.description": "Tout est prêt ! Il ne te reste plus qu'à réserver en quelques clics.",
      "step4.email": "Itinéraire complet envoyé par e-mail :",
      "step4.email.desc": "prêt à être consulté en ligne ou hors connexion.",
      "step4.links": "Liens directs pour réserver :",
      "step4.links.desc": "vols, hébergements, activités, tout est à portée de clic.",
      "step4.modular": "100% modulable :",
      "step4.modular.desc": "tu peux ajuster les dates, changer une activité ou relancer une recherche en un instant.",

      // Why Travliaq
      "whyTravliaq.title": "Pourquoi Travliaq ?",
      "whyTravliaq.subtitle": "La révolution du voyage intelligent est arrivée",
      "whyTravliaq.noPlan.title": "Fini la galère de planification",
      "whyTravliaq.noPlan.desc":
        "Plus de 20 onglets ouverts, plus de comparaisons interminables. L'IA analyse tout pour toi : prix, météo, distances, disponibilités.",
      "whyTravliaq.local.title": "Voyager comme un local",
      "whyTravliaq.local.desc":
        "Nos recommandations te mènent vers les vrais trésors cachés, loin des pièges à touristes. Authentique, pas artificiel.",
      "whyTravliaq.stat": "d'économies en temps de recherche",
      "whyTravliaq.guarantees": "Nos garanties",
      "whyTravliaq.guarantee1": "Meilleurs prix garantis",
      "whyTravliaq.guarantee2": "Itinéraire en moins de 24h",
      "whyTravliaq.guarantee3": "100% personnalisable",
      "whyTravliaq.guarantee4": "Support 7j/7",
      "whyTravliaq.testimonial":
        "« J'ai économisé 15 heures de recherche et 300€ sur mon voyage à Tokyo. Travliaq a trouvé des spots que même mes amis japonais ne connaissaient pas ! »",
      "whyTravliaq.testimonial.author": "Sarah, 26 ans — Tokyo & Kyoto, 10 jours",

      // CTA
      "cta.start": "Commencer mon voyage",
      "cta.create": "Crée ton itinéraire",

      // Comparison Section
      "comparison.title": "Avant / Après Travliaq",
      "comparison.subtitle": "Découvre comment Travliaq transforme ta façon de planifier tes voyages",
      "comparison.before.badge": "Sans Travliaq",
      "comparison.before.title": "Planification traditionnelle",
      "comparison.before.point1": "Des heures passées à comparer des centaines d'onglets",
      "comparison.before.point2": "Risque de surpayer vols et hébergements",
      "comparison.before.point3": "Itinéraire improvisé et stressant",
      "comparison.before.point4": "Aucune garantie de cohérence ou d'optimisation",
      "comparison.after.badge": "Avec Travliaq",
      "comparison.after.title": "Planification intelligente",
      "comparison.after.point1": "Quelques clics pour un itinéraire complet",
      "comparison.after.point2": "Meilleurs prix garantis par l'IA",
      "comparison.after.point3": "Programme optimisé jour par jour",
      "comparison.after.point4": "Support 7j/7 et modifications illimitées",

      // Examples Section
      "examples.title": "Découvre nos voyages",
      "examples.subtitle": "Des exemples concrets d'itinéraires créés par Travliaq",
      "examples.tokyo.title": "Tokyo & Kyoto",
      "examples.tokyo.description": "Culture, gastronomie et temples traditionnels au cœur du Japon.",
      "examples.tokyo.duration": "10 jours",
      "examples.tokyo.travelers": "2 voyageurs",
      "examples.algeria.title": "Sidi Bel Abbès",
      "examples.algeria.description": "Découverte authentique de l'Algérie et de son patrimoine.",
      "examples.algeria.duration": "7 jours",
      "examples.algeria.travelers": "1 voyageur",
      "examples.custom.title": "Ton voyage sur mesure",
      "examples.custom.description": "Crée l'itinéraire parfait selon tes envies et ton budget.",
      "examples.custom.anywhere": "Partout dans le monde",
      "examples.viewTrip": "Voir le voyage",
      "examples.createYours": "Créer mon voyage",

      // Video Section
      "video.title": "Découvre Travliaq en vidéo",
      "video.subtitle": "Vois comment nous simplifions la planification de tes voyages",
      "video.coming": "Vidéo de démonstration",
      "video.demo": "Bientôt disponible",

      // FAQ Section
      "faq.title": "Questions fréquentes",
      "faq.subtitle": "Tout ce que tu dois savoir sur Travliaq",
      "faq.q1": "Comment fonctionne Travliaq ?",
      "faq.a1": "Travliaq utilise l'intelligence artificielle pour analyser tes préférences de voyage (destination, budget, dates, style) et compare en temps réel des milliers d'options de vols, d'hébergements et d'activités. En moins de 24h, tu reçois un itinéraire optimisé jour par jour avec tous les liens de réservation.",
      "faq.q2": "Combien coûte le service ?",
      "faq.a2": "L'utilisation de Travliaq est actuellement gratuite. Tu payes uniquement tes réservations (vols, hôtels, activités) directement auprès des prestataires.",
      "faq.q3": "Puis-je modifier mon itinéraire après réception ?",
      "faq.a3": "Absolument ! Ton itinéraire est 100% modulable. Tu peux ajuster les dates, changer une activité, modifier un hébergement ou même relancer une recherche complète à tout moment.",
      "faq.q4": "Quelles destinations sont disponibles ?",
      "faq.a4": "Travliaq couvre le monde entier ! Que tu rêves de Tokyo, New York, Marrakech ou d'un road trip en Patagonie, notre IA trouve les meilleures options pour n'importe quelle destination.",
      "faq.q5": "Les prix sont-ils vraiment les meilleurs ?",
      "faq.a5": "Oui ! Notre IA compare en temps réel des centaines de sources (compagnies aériennes, plateformes de réservation, hôtels) pour te garantir les meilleurs tarifs. Et si tu trouves moins cher ailleurs, on ajuste ton itinéraire.",
      "faq.q6": "Combien de temps faut-il pour recevoir mon itinéraire ?",
      "faq.a6": "En général, tu reçois ta première proposition dans les 24 heures suivant la soumission de ton questionnaire. Pour les demandes urgentes, nous pouvons accélérer le processus.",
      "faq.more": "Tu as d'autres questions ?",
      "faq.contact": "Contacte-nous",

      // Toast
      "toast.login": "Connectez-vous avec Google pour sauvegarder vos préférences",
      "toast.loginButton": "Se connecter",
      "toast.loginError": "Erreur de connexion: {{error}}",

      // Auth page
      "auth.welcome": "Bienvenue sur Travliaq",
      "auth.subtitle": "Créez votre itinéraire personnalisé en quelques clics",
      "auth.continueWithGoogle": "Continuer avec Google",
      "auth.continueWithLinkedIn": "Continuer avec LinkedIn",
      "auth.continueWithFacebook": "Continuer avec Facebook",
      "auth.useMagicLink": "Connexion sans mot de passe",
      "auth.hideMagicLink": "Masquer le magic link",
      "auth.magicLinkTitle": "Connexion par lien magique",
      "auth.sendMagicLink": "Envoyer le lien",
      "auth.magicLinkSent": "Lien magique envoyé !",
      "auth.magicLinkSentMessage": "Vérifiez votre boîte mail et cliquez sur le lien pour vous connecter.",
      "auth.sendAnotherLink": "Envoyer un autre lien",
      "auth.magicLinkError": "Erreur lors de l'envoi du lien magique",
      "auth.useEmail": "Ou utiliser un email",
      "auth.hideEmailOption": "Masquer l'option email",
      "auth.oauthError": "Erreur lors de la connexion",
      "auth.loginWithEmail": "Connexion par email",
      "auth.signupWithEmail": "Inscription par email",
      "auth.email": "Email",
      "auth.emailPlaceholder": "votre@email.com",
      "auth.password": "Mot de passe",
      "auth.passwordSecure": "Mot de passe sécurisé",
      "auth.passwordMinLength": "Le mot de passe doit contenir au moins 8 caractères",
      "auth.passwordLowercase": "Le mot de passe doit contenir au moins une minuscule",
      "auth.passwordUppercase": "Le mot de passe doit contenir au moins une majuscule",
      "auth.passwordNumber": "Le mot de passe doit contenir au moins un chiffre",
      "auth.login": "Se connecter",
      "auth.signup": "S'inscrire",
      "auth.forgotPassword": "Mot de passe oublié ?",
      "auth.noAccount": "Pas encore de compte ?",
      "auth.signupLink": "S'inscrire",
      "auth.hasAccount": "Déjà un compte ?",
      "auth.loginLink": "Se connecter",
      "auth.footerNote": "En continuant, vous acceptez nos conditions d'utilisation",
      "auth.googleError": "Erreur lors de la connexion avec Google",
      "auth.fillAllFields": "Veuillez remplir tous les champs",
      "auth.invalidEmail": "Adresse email invalide",
      "auth.passwordCriteria": "Le mot de passe ne respecte pas les critères de sécurité",
      "auth.invalidCredentials": "Email ou mot de passe incorrect. Vérifiez que vous avez bien confirmé votre email si vous venez de vous inscrire.",
      "auth.emailNotConfirmed": "Veuillez confirmer votre email avant de vous connecter. Vérifiez votre boîte mail.",
      "auth.sessionError": "Erreur de session. Veuillez réessayer.",
      "auth.loginSuccess": "Connexion réussie",
      "auth.emailAlreadyUsed": "Cette adresse email est déjà utilisée. Si vous vous êtes inscrit avec Google, veuillez vous connecter avec Google.",
      "auth.signupSuccess": "Inscription réussie ! Vérifiez votre email pour confirmer votre compte avant de vous connecter.",
      "auth.signupAutoLogin": "Inscription réussie ! Connexion automatique...",
      "auth.loginError": "Erreur lors de la connexion",
      "auth.signupError": "Erreur lors de l'inscription",

      // Hero name suffix
      "hero.subtitle.afterName":
        ", découvre ton prochain itinéraire personnalisé — vols, hôtels, météo, activités, tout en un seul clic.",

      // Footer
      "footer.ribbon": "Gratuit • Sans engagement • Résultat en 24h",
      "footer.tagline": "L'intelligence artificielle au service de vos voyages exceptionnels",
      "footer.nav": "Navigation",
      "footer.contact": "Contact",
      "footer.legal": "Informations légales",
      "footer.follow": "Suivez-nous",
      "footer.create": "Créer un itinéraire",

      // Common
      "common.back": "Retour",
      "common.backToTop": "Retour en haut",

      // Blog
      "blog.backToBlog": "Retour au blog",
      "blog.readingTime": "{{time}} min de lecture",
      "blog.views": "{{count}} vues",
      "footer.terms": "Conditions Générales de Vente",
      "footer.imprint": "Mentions légales",
      "footer.privacy": "Politique de confidentialité",
      "footer.copyright": "© 2025 Travliaq. Tous droits réservés. Fait avec ❤️ pour les voyageurs authentiques",

      // Discover page
      "discover.title": "DÉCOUVREZ LES VOYAGES",
      "discover.subtitle":
        "Explorez notre collection de voyages uniques, conçus sur mesure pour des expériences inoubliables",
      "discover.searchPlaceholder": "Rechercher une destination, un style de voyage...",
      "discover.filters.budget": "< 800€",
      "discover.filters.group": "En groupe",
      "discover.filters.adventure": "Aventure",
      "discover.filters.nature": "Nature",
      "discover.filters.culture": "Culture",
      "discover.filters.exploration": "Exploration",
      "discover.noTrips": "Aucun voyage trouvé",
      "discover.newBadge": "NOUVEAU",
      "discover.shareLink": "Copier le lien",
      "discover.days": "jours",
      "discover.from": "À partir de",
      "discover.discover": "Découvrir",
      "discover.shareText": "Découvrez ce voyage incroyable : {{destination}}",
      "discover.error": "Erreur",
      "discover.errorLoading": "Impossible de charger les voyages",
      "discover.linkCopied": "Lien copié ! 🎉",
      "discover.linkCopiedDescription": "Le lien du voyage a été copié dans ton presse-papier",

      // Questionnaire
      "q.title": "VOTRE VOYAGE SUR MESURE",
      "q.back": "Retour",
      "q.continue": "Continuer",
      "q.send": "Envoyer 🚀",
      "q.sending": "Envoi en cours...",

      // Q1: Who's traveling
      "q.step1.title": "Qui voyage ? 👥",
      "q.step1.solo": "Solo",
      "q.step1.duo": "Duo",
      "q.step1.group": "Groupe 3-5",
      "q.step1.family": "Famille (enfants <12)",
      "q.step1b.group.title": "Nombre de voyageurs",
      "q.step1b.family.title": "Nombre de personnes (enfants inclus) 👨‍👩‍👧‍👦",

      // Q2: Help needed
      "q.step2.title": "Comment Travliaq peut vous aider ? 🎯",
      "q.step2.desc": "Sélectionnez tous les services nécessaires",
      "q.step2.flights": "Vols",
      "q.step2.accommodation": "Hébergement",
      "q.step2.activities": "Activités & Suggestions",

      // Q3: Destination
      "q.step3.title": "Où souhaitez-vous aller ? 🌍",
      "q.step3.desc": "Destination finale de votre voyage",
      "q.step3.yes": "Oui, j'ai une destination en tête",
      "q.step3.no": "Non, je ne sais pas encore",
      "q.step3.search": "Rechercher une destination...",
      "q.step3.popular": "Destinations populaires",

      // Q4: Departure location
      "q.step4.title": "D'où partez-vous ? ✈️",
      "q.step4.search": "Rechercher votre ville de départ...",
      "q.step4.detect": "Détecter automatiquement",
      "q.step4.detecting": "Détection en cours...",

      // Q5: Climate
      "q.step5.title": "Quel climat préférez-vous ? 🌡️",
      "q.step5.desc": "Sélectionnez tous les climats qui vous plaisent",
      "q.step5.hot": "Chaud (plage, soleil)",
      "q.step5.mild": "Tempéré (doux, agréable)",
      "q.step5.cold": "Froid (montagne, neige)",
      "q.step5.mix": "Mixte (variété)",

      // Q6: Travel affinities
      "q.step6.title": "Quelles sont vos affinités de voyage ? 💫",
      "q.step6.desc": "Choisissez 3 maximum",
      "q.step6.culture": "Culture (musées, monuments)",
      "q.step6.nature": "Nature (randonnée, parcs)",
      "q.step6.gastro": "Gastronomie",
      "q.step6.beach": "Plage & Détente",
      "q.step6.nightlife": "Vie nocturne",
      "q.step6.shopping": "Shopping",
      "q.step6.adventure": "Aventure",
      "q.step6.wellbeing": "Bien-être (spa, yoga)",

      // Q7: Travel ambiance
      "q.step7.title": "Ambiance de voyage souhaitée 🎨",
      "q.step7.romantic": "Romantique (couple)",
      "q.step7.family": "Famille (enfants)",
      "q.step7.friends": "Entre amis (fête)",
      "q.step7.relax": "Détente absolue",
      "q.step7.cultural": "Culturel (apprentissage)",
      "q.step7.adventure": "Aventure (sport)",

      // Q8: Dates
      "q.step8.title": "Quand souhaitez-vous partir ? 📅",
      "q.step8.exact": "J'ai des dates précises",
      "q.step8.approx": "J'ai une période approximative",
      "q.step8.flexible": "Je suis flexible",
      "q.step8.departure": "Date de départ",
      "q.step8.return": "Date de retour",
      "q.step8.approx.title": "Avez-vous une période approximative de départ ?",
      "q.step8.approx.yes": "Oui, j'ai une idée",
      "q.step8.approx.no": "Non, je suis totalement flexible",
      "q.step8.approx.when": "Vers quand souhaiteriez-vous partir ?",
      "q.step8.flex.title": "Quel est votre niveau de flexibilité ? ⏰",
      "q.step8.flex.days": "±3 jours",
      "q.step8.flex.week": "±1 semaine",
      "q.step8.flex.2weeks": "±2 semaines",
      "q.step8.flex.month": "±1 mois",

      // Q9: Duration
      "q.step9.title": "Durée du séjour 📆",
      "q.step9.1week": "1 semaine (5-7 jours)",
      "q.step9.2weeks": "2 semaines (10-14 jours)",
      "q.step9.3weeks": "3 semaines (18-21 jours)",
      "q.step9.custom": "Durée personnalisée",
      "q.step9.nights": "Nombre exact de nuits",

      // Q10: Budget
      "q.step10.title": "Budget par personne 💰",
      "q.step10.desc": "Estimation approximative",
      "q.step10.low": "Petit budget (< 1000€)",
      "q.step10.mid": "Moyen (1000-3000€)",
      "q.step10.high": "Confortable (3000-5000€)",
      "q.step10.premium": "Premium (> 5000€)",
      "q.step10.exact": "Budget précis",
      "q.step10.custom.title": "Budget exact par personne",
      "q.step10.total": "Budget total",
      "q.step10.perperson": "Budget par personne",
      "q.step10.amount": "Montant",
      "q.step10.currency": "Devise",

      // Q11: Travel styles
      "q.step11.title": "Styles de voyage 🎒",
      "q.step11.desc": "Sélectionnez tous ceux qui correspondent",
      "q.step11.backpacker": "Backpacker (auberges, routard)",
      "q.step11.comfort": "Confort (hôtels 3-4⭐)",
      "q.step11.premium": "Premium (hôtels 5⭐, luxe)",
      "q.step11.authentic": "Authentique (local, immersion)",
      "q.step11.classic": "Classique (incontournables)",
      "q.step11.offbeat": "Insolite (hors sentiers battus)",

      // Q12: Rhythm
      "q.step12.title": "Rythme du voyage 🕐",
      "q.step12.slow": "Slow travel (prendre son temps)",
      "q.step12.balanced": "Équilibré (mix détente/visites)",
      "q.step12.intense": "Intense (programme chargé)",

      // Q13: Flight preference
      "q.step13.title": "Préférence de vol ✈️",
      "q.step13.direct": "Direct uniquement",
      "q.step13.1stop": "1 escale max",
      "q.step13.any": "Peu importe (meilleur prix)",

      // Q14: Luggage
      "q.step14.title": "Bagages par voyageur 🧳",
      "q.step14.cabin": "Bagage cabine",
      "q.step14.hold": "Bagage soute",
      "q.step14.both": "Cabine + Soute",

      // Q15: Mobility
      "q.step15.title": "Mobilité & Accessibilité ♿",
      "q.step15.desc": "Sélectionnez tout ce qui s'applique",
      "q.step15.none": "Aucune contrainte",
      "q.step15.wheelchair": "Fauteuil roulant",
      "q.step15.walking": "Difficulté à marcher longtemps",
      "q.step15.elevator": "Besoin ascenseur",
      "q.step15.stairs": "Éviter les escaliers",

      // Q16: Accommodation type
      "q.step16.title": "Type d'hébergement 🏨",
      "q.step16.desc": "Choisissez tous ceux qui vous conviennent",
      "q.step16.hotel": "Hôtel",
      "q.step16.airbnb": "Airbnb/Location",
      "q.step16.hostel": "Auberge de jeunesse",
      "q.step16.resort": "Resort/All-inclusive",
      "q.step16.boutique": "Hôtel boutique",
      "q.step16b.title": "Préférences pour l'hôtel 🏨",
      "q.step16b.desc": "Sélectionnez ce qui vous intéresse",
      "q.step16b.breakfast": "Petit-déjeuner inclus",
      "q.step16b.halfboard": "Demi-pension",
      "q.step16b.fullboard": "Pension complète",
      "q.step16b.allinc": "All-inclusive",
      "q.step16b.room": "Room service",

      // Q17: Comfort
      "q.step17.title": "Niveau de confort attendu 🛏️",
      "q.step17.simple": "Simple (propreté de base)",
      "q.step17.standard": "Standard (confortable)",
      "q.step17.superior": "Supérieur (très confortable)",
      "q.step17.luxury": "Luxe (5 étoiles)",

      // Q18: Neighborhood
      "q.step18.title": "Emplacement de l'hébergement 📍",
      "q.step18.center": "Centre-ville (accès facile)",
      "q.step18.quiet": "Quartier calme (éloigné)",
      "q.step18.transport": "Près des transports",
      "q.step18.attractions": "Près des attractions",

      // Q19: Amenities
      "q.step19.title": "Équipements souhaités 🏊",
      "q.step19.desc": "Sélectionnez tous ceux importants pour vous",
      "q.step19.wifi": "Wi-Fi",
      "q.step19.pool": "Piscine",
      "q.step19.gym": "Salle de sport",
      "q.step19.spa": "Spa",
      "q.step19.restaurant": "Restaurant sur place",
      "q.step19.parking": "Parking",
      "q.step19.ac": "Climatisation",
      "q.step19.kitchen": "Cuisine équipée",

      // Q20: Constraints
      "q.step20.title": "Contraintes & préférences 🎯",
      "q.step20.desc": "Sélectionnez toutes les options importantes",
      "q.step20.halal": "Halal",
      "q.step20.kosher": "Casher",
      "q.step20.vege": "Végétarien",
      "q.step20.vegan": "Vegan",
      "q.step20.gluten": "Sans gluten",
      "q.step20.allergies": "Allergies alimentaires",
      "q.step20.none": "Aucune contrainte",

      // Q21: Additional info
      "q.step21.title": "Informations complémentaires 📝",
      "q.step21.desc": "Précisez vos attentes, contraintes ou préférences particulières (optionnel)",
      "q.step21.placeholder": "Ex: anniversaire, allergies alimentaires, accessibilité spécifique...",

      // Q22: Open comments
      "q.step22.title": "Zone libre 💭",
      "q.step22.desc": "Ajoutez tout commentaire, question ou information utile (optionnel)",
      "q.step22.placeholder": "Partagez vos envies, inspirations, contraintes particulières...",

      // Q23: Email
      "q.step23.title": "Dernière étape ! 📧",
      "q.step23.desc": "Où devons-nous envoyer votre itinéraire personnalisé ?",
      "q.step23.email": "Votre email",
      "q.step23.name": "Votre nom (optionnel)",

      // Errors & Success
      "q.error.quota": "Quota atteint 🚫",
      "q.error.quota.desc":
        "Vous avez atteint votre quota de 2 questionnaires par jour. Revenez demain pour planifier un autre voyage !",
      "q.error.auth": "Connexion requise 🔒",
      "q.error.auth.desc": "Vous devez être connecté pour soumettre un questionnaire.",
      "q.error.validation": "Erreur de validation",
      "q.error.validation.desc": "Certains champs contiennent des données invalides.",
      "q.error.generic": "Erreur",
      "q.error.generic.desc": "Une erreur est survenue lors de l'envoi du questionnaire. Veuillez réessayer.",
      "q.success": "Questionnaire envoyé ! 🎉",
      "q.success.desc": "Nous vous enverrons votre itinéraire personnalisé sous 48h.",

      "questionnaire.pleaseAnswer": "Réponse requise",
      "questionnaire.answerRequired": "Veuillez répondre à cette question avant de continuer",

      // Progress milestones
      "questionnaire.progress.profile": "Profil",
      "questionnaire.progress.destination": "Destination",
      "questionnaire.progress.dates": "Dates",
      "questionnaire.progress.budget": "Budget",
      "questionnaire.progress.flights": "Vols",
      "questionnaire.progress.activities": "Activités",
      "questionnaire.progress.preferences": "Préférences",
      "questionnaire.progress.accommodation": "Hébergement",
      "questionnaire.progress.finalization": "Finalisation",

      // Help with descriptions
      "questionnaire.flights.desc": "Billets d'avion",
      "questionnaire.accommodation.desc": "Hôtels & logements",
      "questionnaire.activities.desc": "Visites & expériences",

      // Luggage details
      "questionnaire.luggage.personalItem.desc": "Sous le siège",
      "questionnaire.luggage.cabin.desc": "Compartiment supérieur",
      "questionnaire.luggage.hold.desc": "Enregistré en soute",
      "questionnaire.luggage.cabinHold.desc": "Cabine + Soute",

      // Mobility options
      "questionnaire.mobility.dontMind": "Peu importe",
      "questionnaire.mobility.walking": "Marche",
      "questionnaire.mobility.publicTransport.full": "Marche/TC principalement",
      "questionnaire.mobility.rentalCar.full": "Location voiture",
      "questionnaire.mobility.taxi.full": "Taxi/VTC",
      "questionnaire.mobility.bike.full": "Vélo",
      "questionnaire.mobility.walking.full": "À pied",
      "questionnaire.mobility.atypical.full": "Transport atypique",
      "questionnaire.mobility.ferry.full": "Bateau/ferry",
      "questionnaire.mobility.electricScooter": "Trottinette électrique",
      "questionnaire.mobility.motorbikeScooter": "Moto/scooter",
      "questionnaire.mobility.touristBus": "Bus touristique",
      "questionnaire.mobility.trainMetro": "Train/métro",

      // Questionnaire - Additional keys
      "questionnaire.connectionRequired": "Connexion requise 🔒",
      "questionnaire.mustBeConnected": "Tu dois être connecté pour soumettre un questionnaire.",
      "questionnaire.submittedTitle": "Questionnaire envoyé ! 🎉",
      "questionnaire.submittedDescription": "Nous t'enverrons ton itinéraire personnalisé sous 48h.",
      "questionnaire.quotaReached": "Quota atteint 🚫",
      "questionnaire.quotaExceeded":
        "Tu as atteint ton quota de 2 questionnaires par jour. Reviens demain pour planifier un autre voyage !",
      "questionnaire.validationError": "Erreur de validation",
      "questionnaire.invalidData": "Certains champs contiennent des données invalides.",
      "questionnaire.error": "Erreur",
      "questionnaire.submissionError": "Une erreur est survenue lors de l'envoi du questionnaire. Veuillez réessayer.",
      "questionnaire.saveAndReturn": "💾 Sauvegarder et revenir plus tard",
      "questionnaire.draftSaved": "Brouillon sauvegardé ! 💾",
      "questionnaire.draftSavedDesc": "Vous pouvez reprendre le questionnaire plus tard",
      "questionnaire.draftRestored": "Brouillon restauré ! 📋",
      "questionnaire.draftRestoredDesc": "Vous avez repris là où vous vous êtes arrêté",
      "questionnaire.resumeDraft": "Reprendre le brouillon ?",
      "questionnaire.whoTraveling": "Qui voyage ? 👥",

      // Review step
      "questionnaire.review.title": "Récapitulatif de ton voyage 📋",
      "questionnaire.review.description": "Vérifie tes réponses avant d'envoyer",
      "questionnaire.review.group": "Groupe de voyageurs",
      "questionnaire.review.destination": "Destination",
      "questionnaire.review.dates": "Dates",
      "questionnaire.review.budget": "Budget",
      "questionnaire.review.preferences": "Préférences",
      "questionnaire.review.accommodation": "Hébergement",
      "questionnaire.review.constraints": "Contraintes",
      "questionnaire.review.contact": "Contact",
      "questionnaire.review.edit": "Modifier",
      "questionnaire.review.emailDesc": "Nous enverrons ton itinéraire à cette adresse",
      "questionnaire.submit": "Envoyer mon questionnaire",
      "questionnaire.submitting": "Envoi en cours...",
      "questionnaire.email": "Email",
      "questionnaire.emailPlaceholder": "ton@email.com",
      "questionnaire.travelGroup": "Groupe de voyage",
      "questionnaire.numberOfTravelers": "Nombre de voyageurs",
      "questionnaire.hasDestination": "Destination en tête",
      "questionnaire.destination": "Destination",
      "questionnaire.departureLocation": "Lieu de départ",
      "questionnaire.datesType": "Type de dates",
      "questionnaire.departureDate": "Date de départ",
      "questionnaire.returnDate": "Date de retour",
      "questionnaire.duration": "Durée",
      "questionnaire.budget": "Budget",
      "questionnaire.budgetAmount": "Montant du budget",
      "questionnaire.budgetCurrency": "Devise",
      "questionnaire.travelAmbiance": "Ambiance de voyage",
      "questionnaire.styles": "Styles",
      "questionnaire.rhythm": "Rythme",
      "questionnaire.schedulePrefs": "Préférences horaires",
      "questionnaire.accommodationType": "Type d'hébergement",
      "questionnaire.hotelPreferences": "Préférences hôtel",
      "questionnaire.comfort": "Confort",
      "questionnaire.security": "Sécurité",
      "questionnaire.mobility": "Mobilité",
      "questionnaire.constraints": "Contraintes",
      "questionnaire.numberOfPeople": "Nombre de personnes 👥",
      "questionnaire.continue": "Continuer",
      "questionnaire.howManyPeople": "Combien de personnes exactement ? 👥",
      "questionnaire.destinationInMind": "Une destination en tête ? 🌍",
      "questionnaire.destinationInMind.description": "Ville ou pays de destination",
      "questionnaire.howCanHelp": "Comment vous aider ? 🎯",
      "questionnaire.howCanHelp.description": "Sélectionnez les services nécessaires",
      "questionnaire.multipleSelectionPossible": "Sélection multiple possible",
      "questionnaire.solo": "Solo",
      "questionnaire.duo": "Duo",
      "questionnaire.group35": "Groupe 3-5",
      "questionnaire.family": "Famille (enfants <12)",
      "questionnaire.3people": "3 personnes",
      "questionnaire.4people": "4 personnes",
      "questionnaire.5people": "5 personnes",
      "questionnaire.yes": "Oui",
      "questionnaire.no": "Non",
      "questionnaire.flights": "Vols",
      "questionnaire.accommodation": "Hébergement",
      "questionnaire.activities": "Activités",
      "questionnaire.destinationDetails": "Renseigne les détails de ta destination 🌍",
      "questionnaire.whereFrom": "Ville de départ ? 📍",
      "questionnaire.cityTooltip":
        "Tu peux saisir n'importe quelle ville, même si elle n'apparaît pas dans la liste. L'IA comprendra ton point de départ si tu l'orthographies correctement.",
      "questionnaire.detecting": "Détection...",
      "questionnaire.myPosition": "Ma position",
      "questionnaire.departureCity": "Ville de départ",
      "questionnaire.whereGoing": "Où vas-tu ? 🌍",
      "questionnaire.destinationTooltip":
        "Tu peux saisir n'importe quelle ville, même si elle n'apparaît pas dans la liste. L'IA comprendra ta destination si tu l'orthographies correctement.",
      "questionnaire.destinationCity": "Ville de destination...",
      "questionnaire.citiesMustBeDifferent": "Les villes de départ et de destination doivent être différentes",
      "questionnaire.climatePreference": "Climat préféré ? 🌡️",
      "questionnaire.climatePreference.description": "Choix multiples possibles",
      "questionnaire.customTrip": "TON VOYAGE SUR MESURE",
      "questionnaire.back": "Retour",
      "questionnaire.backToReview": "Retour au récapitulatif",
      "questionnaire.noCityFound": "Aucune ville trouvée pour",

      // Travelers step
      "questionnaire.travelers.addTravelers": "Ajoute les voyageurs un par un",
      "questionnaire.travelers.adult": "adulte",
      "questionnaire.travelers.adults": "adultes",
      "questionnaire.travelers.child": "enfant",
      "questionnaire.travelers.children": "enfants",
      "questionnaire.travelers.addAdult": "+ Adulte",
      "questionnaire.travelers.addChild": "+ Enfant (<18 ans)",
      "questionnaire.travelers.adultLabel": "Adulte",
      "questionnaire.travelers.childLabel": "Enfant",
      "questionnaire.travelers.childAge": "Âge de l'enfant",
      "questionnaire.travelers.agePlaceholder": "Âge",
      "questionnaire.travelers.childNeedsAdult": "Un enfant doit être accompagné d'au moins un adulte",
      "questionnaire.travelers.childMinAge": "L'âge minimum d'un enfant est 1 an",

      // Climate options
      "questionnaire.climate.hotSunny": "Chaud & ensoleillé",
      "questionnaire.climate.hotSunny.desc": "25-35°C",
      "questionnaire.climate.mildSweet": "Tempéré & doux",
      "questionnaire.climate.mildSweet.desc": "15-25°C",
      "questionnaire.climate.coldSnowy": "Froid & neigeux",
      "questionnaire.climate.coldSnowy.desc": "<10°C",
      "questionnaire.climate.tropicalHumid": "Tropical & humide",
      "questionnaire.climate.tropicalHumid.desc": "Plages & jungle",
      "questionnaire.climate.mountainAltitude": "Montagne & altitude",
      "questionnaire.climate.mountainAltitude.desc": "Air pur",
      "questionnaire.climate.dontMind": "Peu importe",
      "questionnaire.climate.dontMind.desc": "Je suis flexible",

      // Travel affinities
      "questionnaire.affinities.title": "Centres d'intérêt ? 🎯",
      "questionnaire.affinities.description": "Sélectionnez jusqu'à 5 options",
      "questionnaire.affinities.selectUpTo5": "Sélectionne jusqu'à 5 affinités",
      "questionnaire.affinities.dontMind": "Peu importe",
      "questionnaire.affinities.paradiseBeaches": "Plages paradisiaques",
      "questionnaire.affinities.historicCities": "Villes historiques",
      "questionnaire.affinities.natureHiking": "Nature & randonnées",
      "questionnaire.affinities.skiWinterSports": "Ski & sports d'hiver",
      "questionnaire.affinities.safariAnimals": "Safari & animaux",
      "questionnaire.affinities.localGastronomy": "Gastronomie locale",
      "questionnaire.affinities.shoppingFashion": "Shopping & mode",
      "questionnaire.affinities.festivalsEvents": "Festivals & événements",
      "questionnaire.affinities.modernArchitecture": "Architecture moderne",
      "questionnaire.affinities.templesSpirituality": "Temples & spiritualité",
      "questionnaire.affinities.amusementParks": "Parcs d'attractions",
      "questionnaire.affinities.divingSnorkeling": "Plongée & snorkeling",
      "questionnaire.affinities.roadTripFreedom": "Road trip & liberté",
      "questionnaire.affinities.vineyardsWine": "Vignobles & œnotourisme",
      "questionnaire.affinities.desertsLunar": "Déserts & paysages lunaires",
      "questionnaire.affinities.islandsArchipelagos": "Îles & archipels",
      "questionnaire.affinities.photography": "Photographie",
      "questionnaire.affinities.artMuseums": "Art & musées",
      "questionnaire.affinities.waterSports": "Sports nautiques",
      "questionnaire.affinities.fishing": "Pêche",
      "questionnaire.affinities.spasThermal": "Spas & thermalisme",
      "questionnaire.affinities.cruises": "Croisières",
      "questionnaire.affinities.localMarkets": "Marchés locaux",

      // Travel ambiance
      "questionnaire.ambiance.title": "Ambiance souhaitée ? 🎨",
      "questionnaire.ambiance.description": "Type de voyage",
      "questionnaire.ambiance.adventureExotic": "Aventure & dépaysement",
      "questionnaire.ambiance.adventureExotic.desc": "Sortir de ma zone de confort",
      "questionnaire.ambiance.relaxation": "Détente & relaxation",
      "questionnaire.ambiance.relaxation.desc": "Me ressourcer totalement",
      "questionnaire.ambiance.romanceIntimacy": "Romance & intimité",
      "questionnaire.ambiance.romanceIntimacy.desc": "Moments à deux",
      "questionnaire.ambiance.culturalDiscovery": "Découverte culturelle",
      "questionnaire.ambiance.culturalDiscovery.desc": "Apprendre et m'enrichir",
      "questionnaire.ambiance.partyNightlife": "Fête & vie nocturne",
      "questionnaire.ambiance.partyNightlife.desc": "Faire la fête",
      "questionnaire.ambiance.familyConviviality": "Famille & convivialité",
      "questionnaire.ambiance.familyConviviality.desc": "Moments en famille",

      // Dates
      "questionnaire.dates.title": "Dates de voyage ? 📅",
      "questionnaire.dates.description": "Dates fixes ou flexibles",
      "questionnaire.dates.fixed": "Dates fixes",
      "questionnaire.dates.flexible": "Je suis flexible",
      "questionnaire.dates.selectDates": "Sélectionner les dates 🗓️",
      "questionnaire.dates.selectDates.description": "Date de départ puis de retour",
      "questionnaire.dates.selectRange": "Clique et fais glisser pour sélectionner une plage de dates",
      "questionnaire.dates.selected": "Dates sélectionnées :",
      "questionnaire.dates.departure": "Départ :",
      "questionnaire.dates.return": "Retour :",
      "questionnaire.dates.nights": "nuits",
      "questionnaire.dates.reset": "Réinitialiser les dates",
      "questionnaire.dates.pickDeparture": "Sélectionne ta date de départ",
      "questionnaire.dates.pickReturn": "Maintenant, sélectionne ta date de retour",
      "questionnaire.dates.selectedMsg": "Dates sélectionnées !",

      // Flexibility
      "questionnaire.flexibility.title": "Flexibilité des dates ? ⏰",
      "questionnaire.flexibility.description": "Décalage possible pour meilleurs prix",
      "questionnaire.flexibility.souplesse": "Quelle souplesse ? 🔄",
      "questionnaire.flexibility.0days": "±1j",
      "questionnaire.flexibility.3days": "±3j",
      "questionnaire.flexibility.7days": "±7j",
      "questionnaire.flexibility.14days": "±14j",
      "questionnaire.flexibility.30days": "±30j",
      "questionnaire.flexibility.week": "±1 semaine",
      "questionnaire.flexibility.2weeks": "±2 semaines",
      "questionnaire.flexibility.month": "±1 mois",
      "questionnaire.flexibility.hasApproxDate": "Départ approximatif ? 📅",
      "questionnaire.flexibility.hasApproxDate.description": "Avez-vous une idée du moment ?",
      "questionnaire.flexibility.hasApproxDate.yes": "Oui, j'ai une idée",
      "questionnaire.flexibility.hasApproxDate.no": "Non, pas encore",
      "questionnaire.flexibility.approxDate": "Quelle est ta date de départ approximative ? 📆",
      "questionnaire.flexibility.clickDate":
        "Clique sur une date (cette date servira de référence pour ta flexibilité)",
      "questionnaire.flexibility.dateSelected": "Date sélectionnée :",
      "questionnaire.dates.clickInstruction": "Clique sur ta date de départ puis sur ta date de retour ✈️",
      "questionnaire.dates.duration": "Durée du séjour :",
      "questionnaire.dates.days": "jours",

      // Duration
      "questionnaire.duration.title": "Durée du voyage ? 🌙",
      "questionnaire.duration.description": "Nombre de nuits",
      "questionnaire.duration.2nights": "2 nuits",
      "questionnaire.duration.3nights": "3 nuits",
      "questionnaire.duration.4nights": "4 nuits",
      "questionnaire.duration.5nights": "5 nuits",
      "questionnaire.duration.6nights": "6 nuits",
      "questionnaire.duration.7nights": "7 nuits",
      "questionnaire.duration.8to10": "8-10 nuits",
      "questionnaire.duration.11to14": "11-14 nuits",
      "questionnaire.duration.more14": ">14 nuits",
      "questionnaire.duration.exactNights": "Nombre exact de nuits ? 🌉",
      "questionnaire.duration.exactNights.description": "Minimum 15 nuits",
      "questionnaire.duration.specify8to10": "Précise le nombre de nuits 🛏️",
      "questionnaire.duration.specify8to10.description": "Choisis entre 8, 9 ou 10 nuits",
      "questionnaire.duration.specify11to14": "Précise le nombre de nuits 🛏️",
      "questionnaire.duration.specify11to14.description": "Choisis entre 11, 12, 13 ou 14 nuits",
      "questionnaire.duration.8nights": "8 nuits",
      "questionnaire.duration.9nights": "9 nuits",
      "questionnaire.duration.10nights": "10 nuits",
      "questionnaire.duration.11nights": "11 nuits",
      "questionnaire.duration.12nights": "12 nuits",
      "questionnaire.duration.13nights": "13 nuits",
      "questionnaire.duration.14nights": "14 nuits",

      // Budget
      "questionnaire.budget.title": "Budget par personne ? 💰",
      "questionnaire.budget.description": "Budget total hors achats personnels",
      "questionnaire.budget.sliderDescription": "Déplace le curseur pour définir ton budget par personne",
      "questionnaire.budget.perPerson": "par personne",
      "questionnaire.budget.excluding": "Hors achats personnels",
      "questionnaire.budget.less300": "<300€",
      "questionnaire.budget.300to600": "300-600€",
      "questionnaire.budget.600to900": "600-900€",
      "questionnaire.budget.900to1200": "900-1 200€",
      "questionnaire.budget.1200to1800": "1 200-1 800€",
      "questionnaire.budget.more1800": ">1 800€",
      "questionnaire.budget.dontKnow": "Je ne sais pas",
      "questionnaire.budget.precise": "Budget précis",
      "questionnaire.budget.totalBudget": "Budget exact ? 💵",
      "questionnaire.budget.totalBudget.description": "Par personne pour l'ensemble du voyage",
      "questionnaire.budget.entireTrip": "Pour l'ensemble du voyage",
      "questionnaire.budget.amount": "Montant",
      "questionnaire.budget.currency": "Devise",
      "questionnaire.budget.enterPrecise": "Saisir un budget précis",
      "questionnaire.budget.backToSlider": "Retour au curseur",

      // Travel styles
      "questionnaire.styles.title": "Centres d'intérêt activités ? 🎨",
      "questionnaire.styles.description": "Sélectionnez jusqu'à 5 options",
      "questionnaire.styles.selectUpTo5": "Sélectionne jusqu'à 5 styles",
      "questionnaire.styles.nature": "Nature",
      "questionnaire.styles.cultureMuseums": "Culture & musées",
      "questionnaire.styles.food": "Food",
      "questionnaire.styles.beach": "Plage",
      "questionnaire.styles.mountainHiking": "Montagne & rando",
      "questionnaire.styles.photoSpots": "Photo spots",
      "questionnaire.styles.localMarkets": "Vie locale & marchés",
      "questionnaire.styles.sportOutdoor": "Sport & outdoor",
      "questionnaire.styles.wellnessSpa": "Bien-être & spa",
      "questionnaire.styles.nightlife": "Vie nocturne",

      // Rhythm
      "questionnaire.rhythm.title": "Rythme souhaité ? ⏱️",
      "questionnaire.rhythm.description": "Activités par jour",
      "questionnaire.rhythm.selectRhythm": "Sélectionne ton rythme",
      "questionnaire.rhythm.cool": "Cool",
      "questionnaire.rhythm.cool.desc": "1-2 activités/jour",
      "questionnaire.rhythm.relaxed": "Détendu",
      "questionnaire.rhythm.relaxed.desc": "1-2 activités/jour",
      "questionnaire.rhythm.balanced": "Équilibré",
      "questionnaire.rhythm.balanced.desc": "2-3 activités/jour",
      "questionnaire.rhythm.intense": "Intense",
      "questionnaire.rhythm.intense.desc": "3+ activités/jour",

      // Schedule preferences
      "questionnaire.optional": "Optionnel",
      "questionnaire.schedule.title": "Préférences horaires",
      "questionnaire.schedule.description": "Sélectionne jusqu’à 3 options",
      "questionnaire.schedule.maxReached": "Maximum 3 préférences",
      "questionnaire.schedule.earlyBird": "Matinal",
      "questionnaire.schedule.nightOwl": "Couche-tard",
      "questionnaire.schedule.needsSiesta": "Sieste nécessaire",
      "questionnaire.schedule.needsBreaks": "Besoin de pauses",
      "questionnaire.schedule.needsFreeTime": "Besoin de temps libre",
      "questionnaire.schedule.flexibleSchedule": "Horaires flexibles",

      // Flights
      "questionnaire.flights.title": "Préférence vols ? ✈️",
      "questionnaire.flights.description": "Direct ou avec escales",
      "questionnaire.flights.directOnly": "Direct uniquement",
      "questionnaire.flights.max1Stop": "Max 1 escale",
      "questionnaire.flights.dontMind": "Peu importe",

      // Luggage
      "questionnaire.luggage.title": "Bagages par voyageur ? 🧳",
      "questionnaire.luggage.description": "Impacte le prix des vols",
      "questionnaire.luggage.travelers": "voyageur",
      "questionnaire.luggage.travelers_plural": "voyageurs",
      "questionnaire.luggage.traveler": "Voyageur",
      "questionnaire.luggage.personalItem": "Objet personnel uniquement",
      "questionnaire.luggage.cabin": "Bagage cabine uniquement",
      "questionnaire.luggage.hold": "Bagage soute uniquement",
      "questionnaire.luggage.cabinHold": "Cabine + Soute",

      // Mobility
      "questionnaire.mobility.title": "Mobilité ? 🚶",
      "questionnaire.mobility.description": "Modes de transport sur place",
      "questionnaire.mobility.selectMultiple": "Sélection multiple possible",
      "questionnaire.mobility.publicTransport": "Transports en commun",
      "questionnaire.mobility.rentalCar": "Voiture de location",
      "questionnaire.mobility.taxi": "Taxi/VTC",
      "questionnaire.mobility.bike": "Vélo",
      "questionnaire.mobility.atypical": "Transport atypique",
      "questionnaire.mobility.atypical.desc": "Tuk-tuk, scooter, etc.",
      "questionnaire.mobility.ferry": "Bateau/ferry",
      "questionnaire.mobility.incompleteSelection": "Sélection incomplète",
      "questionnaire.mobility.selectOtherTransport":
        "Sélectionne au moins un autre mode de transport en plus du transport atypique ou bateau/ferry.",

      // Accommodation type
      "questionnaire.accommodationType.title": "Type d'hébergement ? 🏨",
      "questionnaire.accommodationType.description": "Sélectionnez 1 ou 2 types",
      "questionnaire.accommodationType.select1or2": "Sélectionnez 1 ou 2 types",
      "questionnaire.accommodationType.hotel": "Hôtel",
      "questionnaire.accommodationType.apartment": "Appartement",
      "questionnaire.accommodationType.villa": "Villa",
      "questionnaire.accommodationType.chalet": "Chalet",
      "questionnaire.accommodationType.condominium": "Condominium",
      "questionnaire.accommodationType.hostel": "Auberge",
      "questionnaire.accommodationType.guesthouse": "Maison d'hôtes/riad",
      "questionnaire.accommodationType.lodge": "Lodge/eco-stay",
      "questionnaire.accommodationType.camping": "Camping/glamping",
      "questionnaire.accommodationType.bedBreakfast": "Chambre d'hôtes",
      "questionnaire.accommodationType.resort": "Resort",
      "questionnaire.accommodationType.dontMind": "Peu importe",

      // Hotel preferences
      "questionnaire.hotelPreferences.title": "Préférences hôtel ? 🏨",
      "questionnaire.hotelPreferences.description": "Services et équipements",
      "questionnaire.hotelPreferences.selectInterested": "Sélectionne ce qui t'intéresse",
      "questionnaire.hotelPreferences.dontMind": "Peu importe",
      "questionnaire.hotelPreferences.breakfast": "Petit-déjeuner inclus",
      "questionnaire.hotelPreferences.halfBoard": "Demi-pension",
      "questionnaire.hotelPreferences.fullBoard": "Pension complète",
      "questionnaire.hotelPreferences.allInclusive": "All-inclusive",
      "questionnaire.hotelPreferences.roomService": "Room service",
      "questionnaire.hotelPreferences.minibar": "Minibar",
      "questionnaire.hotelPreferences.view": "Vue mer/montagne",
      "questionnaire.hotelPreferences.balcony": "Balcon/terrasse",
      "questionnaire.hotelPreferences.concierge": "Service de conciergerie",

      // Comfort
      "questionnaire.comfort.title": "Confort minimum ? ⭐",
      "questionnaire.comfort.description": "Note minimale souhaitée",
      "questionnaire.comfort.rating75": "Note ≥7.5",
      "questionnaire.comfort.rating80": "Note ≥8.0",
      "questionnaire.comfort.rating85": "Note ≥8.5",
      "questionnaire.comfort.dontMind": "Peu importe",

      // Neighborhood
      "questionnaire.neighborhood.title": "Quartier préféré ? 🗺️",
      "questionnaire.neighborhood.description": "Type d'emplacement",
      "questionnaire.neighborhood.quiet": "Calme",
      "questionnaire.neighborhood.centralLively": "Central & animé",
      "questionnaire.neighborhood.nearNatureBeach": "Proche nature/plage",
      "questionnaire.neighborhood.atypicalCharm": "Atypique/charme local",

      // Amenities
      "questionnaire.amenities.title": "Équipements souhaités ? 🛠️",
      "questionnaire.amenities.description": "Choix multiples possibles",
      "questionnaire.amenities.selectAll": "Sélectionne tous ceux qui t'importent",
      "questionnaire.amenities.dontMind": "Peu importe",
      "questionnaire.amenities.reliableWifi": "Wi-Fi fiable",
      "questionnaire.amenities.airConditioning": "Climatisation",
      "questionnaire.amenities.kitchen": "Cuisine",
      "questionnaire.amenities.washingMachine": "Machine à laver",
      "questionnaire.amenities.parking": "Parking",
      "questionnaire.amenities.elevator": "Ascenseur",
      "questionnaire.amenities.reception24": "Réception 24/7",
      "questionnaire.amenities.babyCrib": "Lit bébé",
      "questionnaire.amenities.familyRoom": "Chambre famille",
      "questionnaire.amenities.pool": "Piscine",
      "questionnaire.amenities.gym": "Salle de sport",
      "questionnaire.amenities.spa": "Spa/jacuzzi",
      "questionnaire.amenities.gardenTerrace": "Jardin/terrasse",

      // Children details (for family)
      "questionnaire.children.title": "Détails sur les enfants 👶",
      "questionnaire.children.description": "Pour mieux adapter l'itinéraire",
      "questionnaire.children.howMany": "Combien d'enfants parmi les voyageurs ?",
      "questionnaire.children.child": "Enfant",
      "questionnaire.children.age": "Âge",
      "questionnaire.children.years": "an(s)",
      "questionnaire.children.addChild": "+ Ajouter un enfant",
      "questionnaire.children.removeChild": "Retirer",

      // Security & Safety
      "questionnaire.security.title": "Sécurité & Phobies 🛡️",
      "questionnaire.security.description": "Pour éviter des situations inconfortables",
      "questionnaire.security.selectAll": "Sélectionne ce qui s'applique",
      "questionnaire.security.none": "Aucune contrainte particulière",
      "questionnaire.security.crowds": "Éviter les foules",
      "questionnaire.security.heights": "Peur du vide/hauteurs",
      "questionnaire.security.tunnels": "Phobie des tunnels",
      "questionnaire.security.water": "Peur de l'eau/nager",
      "questionnaire.security.animals": "Peur de certains animaux",
      "questionnaire.security.darkness": "Peur du noir/espaces clos",
      "questionnaire.security.unsafeAreas": "Éviter zones peu sûres",
      "questionnaire.security.extremeActivities": "Pas d'activités extrêmes",

      // Biological clock & Habits
      "questionnaire.biorhythm.title": "Horloge biologique & Habitudes 🕐",
      "questionnaire.biorhythm.description": "Pour un rythme qui te correspond",
      "questionnaire.biorhythm.selectAll": "Sélectionne ce qui te correspond",
      "questionnaire.biorhythm.earlyBird": "Lève-tôt (matin actif)",
      "questionnaire.biorhythm.nightOwl": "Couche-tard (nuit tardive)",
      "questionnaire.biorhythm.naps": "Besoin de siestes",
      "questionnaire.biorhythm.dailyFreeTime": "Temps libre quotidien indispensable",
      "questionnaire.biorhythm.earlyTolerant": "Tolérant aux réveils matinaux",
      "questionnaire.biorhythm.regularMeals": "Repas à heures régulières",
      "questionnaire.biorhythm.flexible": "Flexible, je m'adapte",

      // Constraints
      "questionnaire.constraints.title": "Des contraintes ? 🎯",
      "questionnaire.constraints.description": "Alimentaires, religieuses, accessibilité",
      "questionnaire.constraints.selectAllImportant": "Sélectionne toutes les options importantes",
      "questionnaire.constraints.halal": "Halal",
      "questionnaire.constraints.kosher": "Casher",
      "questionnaire.constraints.vegetarian": "Végétarien",
      "questionnaire.constraints.vegan": "Vegan",
      "questionnaire.constraints.glutenFree": "Sans gluten",
      "questionnaire.constraints.noPork": "Sans porc",
      "questionnaire.constraints.noAlcohol": "Sans alcool",
      "questionnaire.constraints.prayerPlaces": "Lieux de prière",
      "questionnaire.constraints.buddhistTraditions": "Respect traditions bouddhistes",
      "questionnaire.constraints.accessibility": "Accessibilité PMR",
      "questionnaire.constraints.safezones": "Zones sûres",
      "questionnaire.constraints.avoidCar": "Éviter voiture",
      "questionnaire.constraints.localTraditions": "Respect traditions locales",
      "questionnaire.constraints.foodAllergies": "Allergies alimentaires",
      "questionnaire.constraints.dontMind": "Peu importe",

      // Additional info
      "questionnaire.additionalInfo.title": "Infos complémentaires ? 💬",
      "questionnaire.additionalInfo.description": "Précisions importantes à respecter (optionnel)",
      "questionnaire.additionalInfo.descriptionExtended": "Ex : éviter escales, allergies, événement spécial",
      "questionnaire.additionalInfo.optional": "Zone libre (optionnel)",
      "questionnaire.additionalInfo.placeholder": "Partage tes besoins spécifiques...",
      "questionnaire.additionalInfo.skip": "Passer",
      "questionnaire.additionalInfo.characters": "caractères",

      // Email step
      "questionnaire.email.title": "Recevoir mon itinéraire 📧",
      "questionnaire.email.description": "Nous t'enverrons ton itinéraire personnalisé sous 48h",
      "questionnaire.email.placeholder": "ton@email.com",
      "questionnaire.email.send": "Envoyer 🚀",
      "questionnaire.email.sending": "Envoi en cours...",

      // Trip Details page
      "tripDetails.notFound": "Voyage non trouvé",
      "tripDetails.backToTrip": "Retour au voyage",
      "tripDetails.preBookingInfo": "Informations pré-réservation",
      "tripDetails.tripSummary": "Récapitulatif du voyage",
      "tripDetails.days": "jours",
      "tripDetails.travelers": "voyageurs",
      "tripDetails.duration": "Durée",
      "tripDetails.travelersLabel": "Voyageurs",
      "tripDetails.activities": "Activités",
      "tripDetails.totalPrice": "Prix total",
      "tripDetails.included": "Inclus dans le voyage",
      "tripDetails.excluded": "Non inclus",
      "tripDetails.responsibilities": "Responsabilités & Organisation",
      "tripDetails.organizer": "Organisateur du voyage",
      "tripDetails.organizerDesc": "Travliaq SAS agit en qualité d'organisateur de voyage au sens de la directive européenne 2015/2302. À ce titre, nous sommes responsables de la bonne exécution de l'ensemble des prestations incluses dans votre forfait.",
      "tripDetails.globalResponsibility": "Responsabilité globale du séjour",
      "tripDetails.globalResponsibilityDesc": "En cas de problème avec un prestataire (hôtel, compagnie aérienne, etc.), vous vous adressez directement à Travliaq. Nous gérons les réclamations et assurons la coordination avec tous les partenaires.",
      "tripDetails.cancellation": "Politique d'annulation",
      "tripDetails.noRefundPolicy": "Aucun remboursement en cas d'annulation",
      "tripDetails.noRefundDesc": "Les réservations sont fermes et définitives. Aucun remboursement ne sera effectué en cas d'annulation, quelle que soit la raison ou le délai avant le départ.",
      "tripDetails.insuranceRecommendation": "Nous vous recommandons fortement de souscrire une assurance voyage annulation auprès de votre assureur pour vous protéger contre les imprévus.",
      "tripDetails.guarantees": "Garanties & Protections",
      "tripDetails.insolvencyGuarantee": "Garantie contre l'insolvabilité",
      "tripDetails.insolvencyGuaranteeDesc": "Conformément à la réglementation européenne, Travliaq dispose d'une garantie financière qui assure le remboursement des sommes versées et le rapatriement des voyageurs en cas de défaillance de l'entreprise.",
      "tripDetails.rcProInsurance": "Assurance Responsabilité Civile Professionnelle",
      "tripDetails.rcProInsuranceDesc": "Travliaq est couvert par une assurance RC Pro spécifique au secteur du voyage, couvrant les dommages pouvant survenir dans le cadre de l'organisation de votre séjour.",
      "tripDetails.acceptTerms": "J'ai lu et j'accepte les",
      "tripDetails.termsLink": "Conditions Générales de Vente",
      "tripDetails.acceptTerms2": "ainsi que les informations relatives aux responsabilités et garanties détaillées ci-dessus.",
      "tripDetails.proceedToBooking": "Procéder à la réservation",
      "tripDetails.footerNote": "Conformément à la directive européenne 2015/2302 relative aux voyages à forfait, vous bénéficiez de l'ensemble des droits prévus pour les consommateurs.",
    },
  },
  en: {
    translation: {
      // Navigation
      "nav.home": "Home",
      "nav.blog": "Blog",
      "nav.discover": "Discover",
      "nav.admin": "Admin",
      "nav.login": "Login",
      "nav.logout": "Logout",
      "nav.comparison": "Comparison",
      "nav.howItWorks": "How it works",
      "nav.examples": "Examples",
      "nav.whyUs": "Why us",
      "nav.faq": "FAQ",
      "nav.questionnaire": "Questionnaire",

      // Hero Section
      "hero.title": "Your trip,",
      "hero.title.ai": "AI-optimized",
      "hero.subtitle":
        "Discover your next personalized itinerary — flights, hotels, weather, activities, all in one click.",
      "hero.subtitle.user":
        "{{name}}, discover your next personalized itinerary — flights, hotels, weather, activities, all in one click.",
      "hero.cta": "Create your itinerary",

      // How it works
      "howItWorks.title": "How does it work?",
      "howItWorks.description": " simplifies your trip in 4 steps:",
      "howItWorks.step1": "You tell us your wishes (destination, budget, style).",
      "howItWorks.step2":
        "We compare flights, accommodations and activities in real time (prices, weather, distances).",
      "howItWorks.step3": "We create a clear, optimized day-by-day itinerary that respects your budget.",
      "howItWorks.step4":
        "You receive a personalized proposal with detailed pricing and a single link to book everything in a few clicks.",
      "howItWorks.tagline": "Fewer tabs, more adventure.",

      // Steps details
      "step1.title": "Your wishes",
      "step1.description": "Share your travel wishes for a 100% personalized itinerary.",
      "step1.dest": "Tell us your destination:",
      "step1.dest.desc":
        "whether it's Lisbon, Tokyo or just your departure airport, so Travliaq can find the best options.",
      "step1.dates": "Specify your dates:",
      "step1.dates.desc": "fixed or flexible, to optimize prices and weather, and guarantee you perfect timing.",
      "step1.budget": "Share your budget and travel style:",
      "step1.budget.desc": "solo, backpacking, comfort or premium, we adapt each step to your pace and wishes.",

      "step2.title": "Smart search",
      "step2.description": "Our AI analyzes thousands of options to find the best combinations.",
      "step2.scan": "We scan the best options:",
      "step2.scan.desc": "flights, accommodations and activities, from reliable sources updated in real time.",
      "step2.cross": "We cross-reference prices, weather and logistics:",
      "step2.cross.desc": "so each step flows naturally, without wasting time or budget.",
      "step2.filter": "We filter according to your profile:",
      "step2.filter.desc": "solo, backpacker, comfort or premium, each result is adjusted to your priorities.",

      "step3.title": "Optimized itinerary",
      "step3.description": "Receive a detailed day-by-day program, perfectly tailored to your preferences.",
      "step3.program": "Day-by-day program:",
      "step3.program.desc":
        "activities, visits, breaks and meals organized in a logical order, to enjoy without rushing.",
      "step3.budget": "Controlled budget:",
      "step3.budget.desc": "each step is priced to avoid bad surprises, from the flight to the corner café.",
      "step3.tips": "Integrated local tips:",
      "step3.tips.desc": "photo spots, hidden restaurants, smart transport... as if a friend on site was guiding you.",

      "step4.title": "Trip ready to book",
      "step4.description": "Everything is ready! You just need to book in a few clicks.",
      "step4.email": "Complete itinerary sent by email:",
      "step4.email.desc": "ready to be consulted online or offline.",
      "step4.links": "Direct booking links:",
      "step4.links.desc": "flights, accommodations, activities, everything is one click away.",
      "step4.modular": "100% customizable:",
      "step4.modular.desc": "you can adjust dates, change an activity or restart a search in an instant.",

      // Why Travliaq
      "whyTravliaq.title": "Why Travliaq?",
      "whyTravliaq.subtitle": "The smart travel revolution has arrived",
      "whyTravliaq.noPlan.title": "No more planning hassle",
      "whyTravliaq.noPlan.desc":
        "No more 20 open tabs, no more endless comparisons. AI analyzes everything for you: prices, weather, distances, availability.",
      "whyTravliaq.local.title": "Travel like a local",
      "whyTravliaq.local.desc":
        "Our recommendations lead you to real hidden gems, away from tourist traps. Authentic, not artificial.",
      "whyTravliaq.stat": "savings in search time",
      "whyTravliaq.guarantees": "Our guarantees",
      "whyTravliaq.guarantee1": "Best prices guaranteed",
      "whyTravliaq.guarantee2": "Itinerary in less than 24h",
      "whyTravliaq.guarantee3": "100% customizable",
      "whyTravliaq.guarantee4": "24/7 support",
      "whyTravliaq.testimonial":
        "« I saved 15 hours of research and €300 on my trip to Tokyo. Travliaq found spots that even my Japanese friends didn't know! »",
      "whyTravliaq.testimonial.author": "Sarah, 26 years old — Tokyo & Kyoto, 10 days",

      // CTA
      "cta.start": "Start my journey",
      "cta.create": "Create your itinerary",

      // Comparison Section
      "comparison.title": "Before / After Travliaq",
      "comparison.subtitle": "Discover how Travliaq transforms the way you plan your trips",
      "comparison.before.badge": "Without Travliaq",
      "comparison.before.title": "Traditional planning",
      "comparison.before.point1": "Hours spent comparing hundreds of tabs",
      "comparison.before.point2": "Risk of overpaying for flights and accommodations",
      "comparison.before.point3": "Improvised and stressful itinerary",
      "comparison.before.point4": "No guarantee of consistency or optimization",
      "comparison.after.badge": "With Travliaq",
      "comparison.after.title": "Smart planning",
      "comparison.after.point1": "A few clicks for a complete itinerary",
      "comparison.after.point2": "Best prices guaranteed by AI",
      "comparison.after.point3": "Day-by-day optimized program",
      "comparison.after.point4": "24/7 support and unlimited modifications",

      // Examples Section
      "examples.title": "Discover our trips",
      "examples.subtitle": "Concrete examples of itineraries created by Travliaq",
      "examples.tokyo.title": "Tokyo & Kyoto",
      "examples.tokyo.description": "Culture, gastronomy and traditional temples in the heart of Japan.",
      "examples.tokyo.duration": "10 days",
      "examples.tokyo.travelers": "2 travelers",
      "examples.algeria.title": "Sidi Bel Abbès",
      "examples.algeria.description": "Authentic discovery of Algeria and its heritage.",
      "examples.algeria.duration": "7 days",
      "examples.algeria.travelers": "1 traveler",
      "examples.custom.title": "Your custom trip",
      "examples.custom.description": "Create the perfect itinerary according to your wishes and budget.",
      "examples.custom.anywhere": "Anywhere in the world",
      "examples.viewTrip": "View trip",
      "examples.createYours": "Create my trip",

      // Video Section
      "video.title": "Discover Travliaq in video",
      "video.subtitle": "See how we simplify your travel planning",
      "video.coming": "Demo video",
      "video.demo": "Coming soon",

      // FAQ Section
      "faq.title": "Frequently asked questions",
      "faq.subtitle": "Everything you need to know about Travliaq",
      "faq.q1": "How does Travliaq work?",
      "faq.a1": "Travliaq uses artificial intelligence to analyze your travel preferences (destination, budget, dates, style) and compares thousands of flight, accommodation and activity options in real time. In less than 24 hours, you receive an optimized day-by-day itinerary with all booking links.",
      "faq.q2": "How much does the service cost?",
      "faq.a2": "Using Travliaq is currently free. You only pay for your bookings (flights, hotels, activities) directly with the providers.",
      "faq.q3": "Can I modify my itinerary after receiving it?",
      "faq.a3": "Absolutely! Your itinerary is 100% customizable. You can adjust dates, change an activity, modify accommodation or even restart a complete search at any time.",
      "faq.q4": "Which destinations are available?",
      "faq.a4": "Travliaq covers the whole world! Whether you dream of Tokyo, New York, Marrakech or a road trip in Patagonia, our AI finds the best options for any destination.",
      "faq.q5": "Are the prices really the best?",
      "faq.a5": "Yes! Our AI compares hundreds of sources in real time (airlines, booking platforms, hotels) to guarantee you the best rates. And if you find cheaper elsewhere, we adjust your itinerary.",
      "faq.q6": "How long does it take to receive my itinerary?",
      "faq.a6": "Generally, you receive your first proposal within 24 hours of submitting your questionnaire. For urgent requests, we can speed up the process.",
      "faq.more": "Have more questions?",
      "faq.contact": "Contact us",

      // Toast
      "toast.login": "Sign in with Google to save your preferences",
      "toast.loginButton": "Sign in",
      "toast.loginError": "Login error: {{error}}",

      // Auth page
      "auth.welcome": "Welcome to Travliaq",
      "auth.subtitle": "Create your personalized itinerary in a few clicks",
      "auth.continueWithGoogle": "Continue with Google",
      "auth.continueWithLinkedIn": "Continue with LinkedIn",
      "auth.continueWithFacebook": "Continue with Facebook",
      "auth.useMagicLink": "Passwordless login",
      "auth.hideMagicLink": "Hide magic link",
      "auth.magicLinkTitle": "Magic link login",
      "auth.sendMagicLink": "Send link",
      "auth.magicLinkSent": "Magic link sent!",
      "auth.magicLinkSentMessage": "Check your mailbox and click the link to sign in.",
      "auth.sendAnotherLink": "Send another link",
      "auth.magicLinkError": "Error sending magic link",
      "auth.useEmail": "Or use email",
      "auth.hideEmailOption": "Hide email option",
      "auth.oauthError": "Authentication error",
      "auth.loginWithEmail": "Login with email",
      "auth.signupWithEmail": "Sign up with email",
      "auth.email": "Email",
      "auth.emailPlaceholder": "your@email.com",
      "auth.password": "Password",
      "auth.passwordSecure": "Secure password",
      "auth.passwordMinLength": "Password must be at least 8 characters long",
      "auth.passwordLowercase": "Password must contain at least one lowercase letter",
      "auth.passwordUppercase": "Password must contain at least one uppercase letter",
      "auth.passwordNumber": "Password must contain at least one number",
      "auth.login": "Sign in",
      "auth.signup": "Sign up",
      "auth.forgotPassword": "Forgot password?",
      "auth.noAccount": "Don't have an account?",
      "auth.signupLink": "Sign up",
      "auth.hasAccount": "Already have an account?",
      "auth.loginLink": "Sign in",
      "auth.footerNote": "By continuing, you accept our terms of service",
      "auth.googleError": "Error signing in with Google",
      "auth.fillAllFields": "Please fill in all fields",
      "auth.invalidEmail": "Invalid email address",
      "auth.passwordCriteria": "Password does not meet security criteria",
      "auth.invalidCredentials": "Invalid email or password. Make sure you confirmed your email if you just signed up.",
      "auth.emailNotConfirmed": "Please confirm your email before signing in. Check your mailbox.",
      "auth.sessionError": "Session error. Please try again.",
      "auth.loginSuccess": "Successfully signed in",
      "auth.emailAlreadyUsed": "This email address is already in use. If you signed up with Google, please sign in with Google.",
      "auth.signupSuccess": "Successfully signed up! Check your email to confirm your account before signing in.",
      "auth.signupAutoLogin": "Successfully signed up! Signing in automatically...",
      "auth.loginError": "Error signing in",
      "auth.signupError": "Error signing up",

      // Hero name suffix
      "hero.subtitle.afterName":
        ", discover your next personalized itinerary — flights, hotels, weather, activities, all in one click.",

      // Footer
      "footer.ribbon": "Free • No commitment • Result in 24h",
      "footer.tagline": "Artificial intelligence at the service of your exceptional travels",
      "footer.nav": "Navigation",
      "footer.contact": "Contact",
      "footer.legal": "Legal information",
      "footer.follow": "Follow us",
      "footer.create": "Create an itinerary",
      "footer.terms": "Terms and Conditions",
      "footer.imprint": "Legal Notice",
      "footer.privacy": "Privacy Policy",
      "footer.copyright": "© 2025 Travliaq. All rights reserved. Made with ❤️ for authentic travelers",

      // Common
      "common.back": "Back",
      "common.backToTop": "Back to top",

      // Blog
      "blog.backToBlog": "Back to blog",
      "blog.readingTime": "{{time}} min read",
      "blog.views": "{{count}} views",

      // Discover page
      "discover.title": "DISCOVER TRIPS",
      "discover.subtitle": "Explore our collection of unique trips, custom-designed for unforgettable experiences",
      "discover.searchPlaceholder": "Search for a destination, travel style...",
      "discover.filters.budget": "< €800",
      "discover.filters.group": "Group",
      "discover.filters.adventure": "Adventure",
      "discover.filters.nature": "Nature",
      "discover.filters.culture": "Culture",
      "discover.filters.exploration": "Exploration",
      "discover.noTrips": "No trips found",
      "discover.newBadge": "NEW",
      "discover.shareLink": "Copy link",
      "discover.days": "days",
      "discover.from": "From",
      "discover.discover": "Discover",
      "discover.shareText": "Discover this incredible trip: {{destination}}",
      "discover.error": "Error",
      "discover.errorLoading": "Unable to load trips",
      "discover.linkCopied": "Link copied! 🎉",
      "discover.linkCopiedDescription": "The trip link has been copied to your clipboard",

      // Questionnaire
      "q.title": "YOUR CUSTOM TRIP",
      "q.back": "Back",
      "q.continue": "Continue",
      "q.send": "Send 🚀",
      "q.sending": "Sending...",

      // Q1: Who's traveling
      "q.step1.title": "Who's traveling? 👥",
      "q.step1.solo": "Solo",
      "q.step1.duo": "Duo",
      "q.step1.group": "Group 3-5",
      "q.step1.family": "Family (children <12)",
      "q.step1b.group.title": "Number of travelers",
      "q.step1b.family.title": "Number of people (children included) 👨‍👩‍👧‍👦",

      // Q2: Help needed
      "q.step2.title": "How can Travliaq help you? 🎯",
      "q.step2.desc": "Select all services needed",
      "q.step2.flights": "Flights",
      "q.step2.accommodation": "Accommodation",
      "q.step2.activities": "Activities & Suggestions",

      // Q3: Destination
      "q.step3.title": "Where do you want to go? 🌍",
      "q.step3.desc": "Final destination of your trip",
      "q.step3.yes": "Yes, I have a destination in mind",
      "q.step3.no": "No, I don't know yet",
      "q.step3.search": "Search for a destination...",
      "q.step3.popular": "Popular destinations",

      // Q4: Departure location
      "q.step4.title": "Where are you departing from? ✈️",
      "q.step4.search": "Search your departure city...",
      "q.step4.detect": "Auto-detect",
      "q.step4.detecting": "Detecting...",

      // Q5: Climate
      "q.step5.title": "What climate do you prefer? 🌡️",
      "q.step5.desc": "Select all climates you like",
      "q.step5.hot": "Hot (beach, sun)",
      "q.step5.mild": "Mild (gentle, pleasant)",
      "q.step5.cold": "Cold (mountains, snow)",
      "q.step5.mix": "Mixed (variety)",

      // Q6: Travel affinities
      "q.step6.title": "What are your travel interests? 💫",
      "q.step6.desc": "Choose maximum 3",
      "q.step6.culture": "Culture (museums, monuments)",
      "q.step6.nature": "Nature (hiking, parks)",
      "q.step6.gastro": "Gastronomy",
      "q.step6.beach": "Beach & Relaxation",
      "q.step6.nightlife": "Nightlife",
      "q.step6.shopping": "Shopping",
      "q.step6.adventure": "Adventure",
      "q.step6.wellbeing": "Wellness (spa, yoga)",

      // Q7: Travel ambiance
      "q.step7.title": "Desired travel atmosphere 🎨",
      "q.step7.romantic": "Romantic (couple)",
      "q.step7.family": "Family (with children)",
      "q.step7.friends": "With friends (party)",
      "q.step7.relax": "Total relaxation",
      "q.step7.cultural": "Cultural (learning)",
      "q.step7.adventure": "Adventure (sports)",

      // Q8: Dates
      "q.step8.title": "When do you want to travel? 📅",
      "q.step8.exact": "I have specific dates",
      "q.step8.approx": "I have an approximate period",
      "q.step8.flexible": "I'm flexible",
      "q.step8.departure": "Departure date",
      "q.step8.return": "Return date",
      "q.step8.approx.title": "Do you have an approximate departure period?",
      "q.step8.approx.yes": "Yes, I have an idea",
      "q.step8.approx.no": "No, I'm totally flexible",
      "q.step8.approx.when": "When would you like to leave?",
      "q.step8.flex.title": "What is your flexibility level? ⏰",
      "q.step8.flex.days": "±3 days",
      "q.step8.flex.week": "±1 week",
      "q.step8.flex.2weeks": "±2 weeks",
      "q.step8.flex.month": "±1 month",

      // Q9: Duration
      "q.step9.title": "Trip duration 📆",
      "q.step9.1week": "1 week (5-7 days)",
      "q.step9.2weeks": "2 weeks (10-14 days)",
      "q.step9.3weeks": "3 weeks (18-21 days)",
      "q.step9.custom": "Custom duration",
      "q.step9.nights": "Exact number of nights",

      // Q10: Budget
      "q.step10.title": "Budget per person 💰",
      "q.step10.desc": "Rough estimate",
      "q.step10.low": "Low budget (< €1000)",
      "q.step10.mid": "Medium (€1000-3000)",
      "q.step10.high": "Comfortable (€3000-5000)",
      "q.step10.premium": "Premium (> €5000)",
      "q.step10.exact": "Precise budget",
      "q.step10.custom.title": "Exact budget per person",
      "q.step10.total": "Total budget",
      "q.step10.perperson": "Budget per person",
      "q.step10.amount": "Amount",
      "q.step10.currency": "Currency",

      // Q11: Travel styles
      "q.step11.title": "Travel styles 🎒",
      "q.step11.desc": "Select all that apply",
      "q.step11.backpacker": "Backpacker (hostels, budget)",
      "q.step11.comfort": "Comfort (3-4⭐ hotels)",
      "q.step11.premium": "Premium (5⭐ hotels, luxury)",
      "q.step11.authentic": "Authentic (local, immersion)",
      "q.step11.classic": "Classic (must-sees)",
      "q.step11.offbeat": "Off the beaten path",

      // Q12: Rhythm
      "q.step12.title": "Trip pace 🕐",
      "q.step12.slow": "Slow travel (take your time)",
      "q.step12.balanced": "Balanced (mix relaxation/visits)",
      "q.step12.intense": "Intense (packed schedule)",

      // Q13: Flight preference
      "q.step13.title": "Flight preference ✈️",
      "q.step13.direct": "Direct only",
      "q.step13.1stop": "Max 1 layover",
      "q.step13.any": "Doesn't matter (best price)",

      // Q14: Luggage
      "q.step14.title": "Luggage per traveler 🧳",
      "q.step14.cabin": "Cabin luggage",
      "q.step14.hold": "Checked luggage",
      "q.step14.both": "Cabin + Checked",

      // Q15: Mobility
      "q.step15.title": "Mobility & Accessibility ♿",
      "q.step15.desc": "Select all that apply",
      "q.step15.none": "No constraints",
      "q.step15.wheelchair": "Wheelchair",
      "q.step15.walking": "Difficulty walking long distances",
      "q.step15.elevator": "Need elevator",
      "q.step15.stairs": "Avoid stairs",

      // Q16: Accommodation type
      "q.step16.title": "Accommodation type 🏨",
      "q.step16.desc": "Choose all that suit you",
      "q.step16.hotel": "Hotel",
      "q.step16.airbnb": "Airbnb/Rental",
      "q.step16.hostel": "Hostel",
      "q.step16.resort": "Resort/All-inclusive",
      "q.step16.boutique": "Boutique hotel",
      "q.step16b.title": "Hotel preferences 🏨",
      "q.step16b.desc": "Select what interests you",
      "q.step16b.breakfast": "Breakfast included",
      "q.step16b.halfboard": "Half-board",
      "q.step16b.fullboard": "Full-board",
      "q.step16b.allinc": "All-inclusive",
      "q.step16b.room": "Room service",

      // Q17: Comfort
      "q.step17.title": "Expected comfort level 🛏️",
      "q.step17.simple": "Simple (basic cleanliness)",
      "q.step17.standard": "Standard (comfortable)",
      "q.step17.superior": "Superior (very comfortable)",
      "q.step17.luxury": "Luxury (5 stars)",

      // Q18: Neighborhood
      "q.step18.title": "Accommodation location 📍",
      "q.step18.center": "City center (easy access)",
      "q.step18.quiet": "Quiet area (remote)",
      "q.step18.transport": "Near transport",
      "q.step18.attractions": "Near attractions",

      // Q19: Amenities
      "q.step19.title": "Desired amenities 🏊",
      "q.step19.desc": "Select all important to you",
      "q.step19.wifi": "Wi-Fi",
      "q.step19.pool": "Pool",
      "q.step19.gym": "Gym",
      "q.step19.spa": "Spa",
      "q.step19.restaurant": "On-site restaurant",
      "q.step19.parking": "Parking",
      "q.step19.ac": "Air conditioning",
      "q.step19.kitchen": "Equipped kitchen",

      // Children details (for family)
      "questionnaire.children.title": "Children Details 👶",
      "questionnaire.children.description": "To better adapt the itinerary",
      "questionnaire.children.howMany": "How many children among the travelers?",
      "questionnaire.children.child": "Child",
      "questionnaire.children.age": "Age",
      "questionnaire.children.years": "year(s)",
      "questionnaire.children.addChild": "+ Add a child",
      "questionnaire.children.removeChild": "Remove",

      // Security & Safety
      "questionnaire.security.title": "Security & Phobias 🛡️",
      "questionnaire.security.description": "To avoid uncomfortable situations",
      "questionnaire.security.selectAll": "Select what applies",
      "questionnaire.security.none": "No particular constraints",
      "questionnaire.security.crowds": "Avoid crowds",
      "questionnaire.security.heights": "Fear of heights",
      "questionnaire.security.tunnels": "Tunnel phobia",
      "questionnaire.security.water": "Fear of water/swimming",
      "questionnaire.security.animals": "Fear of certain animals",
      "questionnaire.security.darkness": "Fear of darkness/confined spaces",
      "questionnaire.security.unsafeAreas": "Avoid unsafe areas",
      "questionnaire.security.extremeActivities": "No extreme activities",

      // Biological clock & Habits
      "questionnaire.biorhythm.title": "Biological Clock & Habits 🕐",
      "questionnaire.biorhythm.description": "For a rhythm that suits you",
      "questionnaire.biorhythm.selectAll": "Select what fits you",
      "questionnaire.biorhythm.earlyBird": "Early bird (active morning)",
      "questionnaire.biorhythm.nightOwl": "Night owl (late nights)",
      "questionnaire.biorhythm.naps": "Need naps",
      "questionnaire.biorhythm.dailyFreeTime": "Daily free time essential",
      "questionnaire.biorhythm.earlyTolerant": "Tolerant to early wake-ups",
      "questionnaire.biorhythm.regularMeals": "Regular meal times",
      "questionnaire.biorhythm.flexible": "Flexible, I adapt",

      // Q20: Constraints
      "q.step20.title": "Constraints & preferences 🎯",
      "q.step20.desc": "Select all important options",
      "q.step20.halal": "Halal",
      "q.step20.kosher": "Kosher",
      "q.step20.vege": "Vegetarian",
      "q.step20.vegan": "Vegan",
      "q.step20.gluten": "Gluten-free",
      "q.step20.allergies": "Food allergies",
      "q.step20.none": "No constraints",

      // Q21: Additional info
      "q.step21.title": "Additional information 📝",
      "q.step21.desc": "Specify your expectations, constraints or special preferences (optional)",
      "q.step21.placeholder": "E.g.: birthday, food allergies, specific accessibility...",

      // Q22: Open comments
      "q.step22.title": "Free text 💭",
      "q.step22.desc": "Add any comments, questions or useful information (optional)",
      "q.step22.placeholder": "Share your desires, inspirations, special constraints...",

      // Q23: Email
      "q.step23.title": "Last step! 📧",
      "q.step23.desc": "Where should we send your personalized itinerary?",
      "q.step23.email": "Your email",
      "q.step23.name": "Your name (optional)",

      // Errors & Success
      "q.error.quota": "Quota reached 🚫",
      "q.error.quota.desc":
        "You have reached your quota of 2 questionnaires per day. Come back tomorrow to plan another trip!",
      "q.error.auth": "Login required 🔒",
      "q.error.auth.desc": "You must be logged in to submit a questionnaire.",
      "q.error.validation": "Validation error",
      "q.error.validation.desc": "Some fields contain invalid data.",
      "q.error.generic": "Error",
      "q.error.generic.desc": "An error occurred while submitting the questionnaire. Please try again.",
      "q.success": "Questionnaire submitted! 🎉",
      "q.success.desc": "We'll send you your personalized itinerary within 48 hours.",

      "questionnaire.pleaseAnswer": "Answer required",
      "questionnaire.answerRequired": "Please answer this question before continuing",

      // Progress milestones
      "questionnaire.progress.profile": "Profile",
      "questionnaire.progress.destination": "Destination",
      "questionnaire.progress.dates": "Dates",
      "questionnaire.progress.budget": "Budget",
      "questionnaire.progress.flights": "Flights",
      "questionnaire.progress.activities": "Activities",
      "questionnaire.progress.preferences": "Preferences",
      "questionnaire.progress.accommodation": "Accommodation",
      "questionnaire.progress.finalization": "Finalization",

      // Help with descriptions
      "questionnaire.flights.desc": "Flight tickets",
      "questionnaire.accommodation.desc": "Hotels & lodging",
      "questionnaire.activities.desc": "Visits & experiences",

      // Luggage details
      "questionnaire.luggage.personalItem.desc": "Under the seat",
      "questionnaire.luggage.cabin.desc": "Overhead bin",
      "questionnaire.luggage.hold.desc": "Checked luggage",
      "questionnaire.luggage.cabinHold.desc": "Cabin + Checked",

      // Mobility options
      "questionnaire.mobility.dontMind": "Don't mind",
      "questionnaire.mobility.walking": "Walking",
      "questionnaire.mobility.publicTransport.full": "Walking/public transport mainly",
      "questionnaire.mobility.rentalCar.full": "Rental car",
      "questionnaire.mobility.taxi.full": "Taxi/Uber",
      "questionnaire.mobility.bike.full": "Bike",
      "questionnaire.mobility.walking.full": "Walking",
      "questionnaire.mobility.atypical.full": "Atypical transport",
      "questionnaire.mobility.ferry.full": "Boat/ferry",
      "questionnaire.mobility.incompleteSelection": "Incomplete selection",
      "questionnaire.mobility.selectOtherTransport":
        "Select at least one other mode of transport in addition to atypical transport or boat/ferry.",
      "questionnaire.mobility.electricScooter": "Electric scooter",
      "questionnaire.mobility.motorbikeScooter": "Motorbike/scooter",
      "questionnaire.mobility.touristBus": "Tourist bus",
      "questionnaire.mobility.trainMetro": "Train/metro",

      // Questionnaire - Additional keys
      "questionnaire.connectionRequired": "Connection required 🔒",
      "questionnaire.mustBeConnected": "You must be logged in to submit a questionnaire.",
      "questionnaire.submittedTitle": "Questionnaire submitted! 🎉",
      "questionnaire.submittedDescription": "We will send you your personalized itinerary within 48h.",
      "questionnaire.quotaReached": "Quota reached 🚫",
      "questionnaire.quotaExceeded":
        "You have reached your quota of 2 questionnaires per day. Come back tomorrow to plan another trip!",
      "questionnaire.validationError": "Validation error",
      "questionnaire.invalidData": "Some fields contain invalid data.",
      "questionnaire.error": "Error",
      "questionnaire.submissionError": "An error occurred while submitting the questionnaire. Please try again.",
      "questionnaire.saveAndReturn": "💾 Save and return later",
      "questionnaire.draftSaved": "Draft saved! 💾",
      "questionnaire.draftSavedDesc": "You can resume the questionnaire later",
      "questionnaire.draftRestored": "Draft restored! 📋",
      "questionnaire.draftRestoredDesc": "You've resumed where you left off",
      "questionnaire.resumeDraft": "Resume draft?",
      "questionnaire.whoTraveling": "Who's traveling? 👥",

      // Review step
      "questionnaire.review.title": "Trip summary 📋",
      "questionnaire.review.description": "Review your answers before submitting",
      "questionnaire.review.group": "Travel group",
      "questionnaire.review.destination": "Destination",
      "questionnaire.review.dates": "Dates",
      "questionnaire.review.budget": "Budget",
      "questionnaire.review.preferences": "Preferences",
      "questionnaire.review.accommodation": "Accommodation",
      "questionnaire.review.constraints": "Constraints",
      "questionnaire.review.contact": "Contact",
      "questionnaire.review.edit": "Edit",
      "questionnaire.review.emailDesc": "We'll send your itinerary to this address",
      "questionnaire.submit": "Send my questionnaire",
      "questionnaire.submitting": "Sending...",
      "questionnaire.email": "Email",
      "questionnaire.emailPlaceholder": "your@email.com",
      "questionnaire.travelGroup": "Travel group",
      "questionnaire.numberOfTravelers": "Number of travelers",
      "questionnaire.hasDestination": "Destination in mind",
      "questionnaire.destination": "Destination",
      "questionnaire.departureLocation": "Departure location",
      "questionnaire.datesType": "Dates type",
      "questionnaire.departureDate": "Departure date",
      "questionnaire.returnDate": "Return date",
      "questionnaire.duration": "Duration",
      "questionnaire.budget": "Budget",
      "questionnaire.budgetAmount": "Budget amount",
      "questionnaire.budgetCurrency": "Currency",
      "questionnaire.travelAmbiance": "Travel vibe",
      "questionnaire.styles": "Styles",
      "questionnaire.rhythm": "Rhythm",
      "questionnaire.schedulePrefs": "Schedule preferences",
      "questionnaire.accommodationType": "Accommodation type",
      "questionnaire.hotelPreferences": "Hotel preferences",
      "questionnaire.comfort": "Comfort",
      "questionnaire.security": "Security",
      "questionnaire.mobility": "Mobility",
      "questionnaire.constraints": "Constraints",
      "questionnaire.numberOfPeople": "Number of people 👥",
      "questionnaire.continue": "Continue",
      "questionnaire.howManyPeople": "How many people exactly? 👥",
      "questionnaire.destinationInMind": "Destination in mind? 🌍",
      "questionnaire.destinationInMind.description": "City or country you want to visit",
      "questionnaire.howCanHelp": "How can we help? 🎯",
      "questionnaire.howCanHelp.description": "Select the services you need",
      "questionnaire.multipleSelectionPossible": "Multiple selection possible",
      "questionnaire.solo": "Solo",
      "questionnaire.duo": "Duo",
      "questionnaire.group35": "Group 3-5",
      "questionnaire.family": "Family (children <12)",
      "questionnaire.3people": "3 people",
      "questionnaire.4people": "4 people",
      "questionnaire.5people": "5 people",
      "questionnaire.yes": "Yes",
      "questionnaire.no": "No",
      "questionnaire.flights": "Flights",
      "questionnaire.accommodation": "Accommodation",
      "questionnaire.activities": "Activities",
      "questionnaire.destinationDetails": "Enter your destination details 🌍",
      "questionnaire.whereFrom": "Departure city? 📍",
      "questionnaire.cityTooltip":
        "You can enter any city, even if it doesn't appear in the list. The AI will understand your starting point if you spell it correctly.",
      "questionnaire.detecting": "Detecting...",
      "questionnaire.myPosition": "My position",
      "questionnaire.departureCity": "Departure city",
      "questionnaire.whereGoing": "Where are you going? 🌍",
      "questionnaire.destinationTooltip":
        "You can enter any city, even if it doesn't appear in the list. The AI will understand your destination if you spell it correctly.",
      "questionnaire.destinationCity": "Destination city...",
      "questionnaire.citiesMustBeDifferent": "Departure and destination cities must be different",
      "questionnaire.climatePreference": "Preferred climate? 🌡️",
      "questionnaire.climatePreference.description": "Multiple choices possible",
      "questionnaire.customTrip": "YOUR CUSTOM TRIP",
      "questionnaire.back": "Back",
      "questionnaire.backToReview": "Back to summary",
      "questionnaire.noCityFound": "No city found for",

      // Travelers step
      "questionnaire.travelers.addTravelers": "Add travelers one by one",
      "questionnaire.travelers.adult": "adult",
      "questionnaire.travelers.adults": "adults",
      "questionnaire.travelers.child": "child",
      "questionnaire.travelers.children": "children",
      "questionnaire.travelers.addAdult": "+ Adult",
      "questionnaire.travelers.addChild": "+ Child (<18 years)",
      "questionnaire.travelers.adultLabel": "Adult",
      "questionnaire.travelers.childLabel": "Child",
      "questionnaire.travelers.childAge": "Child's age",
      "questionnaire.travelers.agePlaceholder": "Age",
      "questionnaire.travelers.childNeedsAdult": "A child must be accompanied by at least one adult",
      "questionnaire.travelers.childMinAge": "The minimum age for a child is 1 year",

      // Climate options
      "questionnaire.climate.hotSunny": "Hot & sunny",
      "questionnaire.climate.hotSunny.desc": "25-35°C",
      "questionnaire.climate.mildSweet": "Mild & sweet",
      "questionnaire.climate.mildSweet.desc": "15-25°C",
      "questionnaire.climate.coldSnowy": "Cold & snowy",
      "questionnaire.climate.coldSnowy.desc": "<10°C",
      "questionnaire.climate.tropicalHumid": "Tropical & humid",
      "questionnaire.climate.tropicalHumid.desc": "Beaches & jungle",
      "questionnaire.climate.mountainAltitude": "Mountain & altitude",
      "questionnaire.climate.mountainAltitude.desc": "Fresh air",
      "questionnaire.climate.dontMind": "Don't mind",
      "questionnaire.climate.dontMind.desc": "I'm flexible",

      // Travel affinities
      "questionnaire.affinities.title": "Travel interests? 🎯",
      "questionnaire.affinities.description": "Select up to 5 options",
      "questionnaire.affinities.selectUpTo5": "Select up to 5 affinities",
      "questionnaire.affinities.dontMind": "Don't mind",
      "questionnaire.affinities.paradiseBeaches": "Paradise beaches",
      "questionnaire.affinities.historicCities": "Historic cities",
      "questionnaire.affinities.natureHiking": "Nature & hiking",
      "questionnaire.affinities.skiWinterSports": "Skiing & winter sports",
      "questionnaire.affinities.safariAnimals": "Safari & animals",
      "questionnaire.affinities.localGastronomy": "Local gastronomy",
      "questionnaire.affinities.shoppingFashion": "Shopping & fashion",
      "questionnaire.affinities.festivalsEvents": "Festivals & events",
      "questionnaire.affinities.modernArchitecture": "Modern architecture",
      "questionnaire.affinities.templesSpirituality": "Temples & spirituality",
      "questionnaire.affinities.amusementParks": "Amusement parks",
      "questionnaire.affinities.divingSnorkeling": "Diving & snorkeling",
      "questionnaire.affinities.roadTripFreedom": "Road trip & freedom",
      "questionnaire.affinities.vineyardsWine": "Vineyards & wine tourism",
      "questionnaire.affinities.desertsLunar": "Deserts & lunar landscapes",
      "questionnaire.affinities.islandsArchipelagos": "Islands & archipelagos",
      "questionnaire.affinities.photography": "Photography",
      "questionnaire.affinities.artMuseums": "Art & museums",
      "questionnaire.affinities.waterSports": "Water sports",
      "questionnaire.affinities.fishing": "Fishing",
      "questionnaire.affinities.spasThermal": "Spas & thermal baths",
      "questionnaire.affinities.cruises": "Cruises",
      "questionnaire.affinities.localMarkets": "Local markets",

      // Travel ambiance
      "questionnaire.ambiance.title": "Desired ambiance? 🎨",
      "questionnaire.ambiance.description": "Trip atmosphere",
      "questionnaire.ambiance.adventureExotic": "Adventure & exotic",
      "questionnaire.ambiance.adventureExotic.desc": "Get out of my comfort zone",
      "questionnaire.ambiance.relaxation": "Relaxation",
      "questionnaire.ambiance.relaxation.desc": "Completely recharge",
      "questionnaire.ambiance.romanceIntimacy": "Romance & intimacy",
      "questionnaire.ambiance.romanceIntimacy.desc": "Moments for two",
      "questionnaire.ambiance.culturalDiscovery": "Cultural discovery",
      "questionnaire.ambiance.culturalDiscovery.desc": "Learn and grow",
      "questionnaire.ambiance.partyNightlife": "Party & nightlife",
      "questionnaire.ambiance.partyNightlife.desc": "Party time",
      "questionnaire.ambiance.familyConviviality": "Family & conviviality",
      "questionnaire.ambiance.familyConviviality.desc": "Family moments",

      // Dates
      "questionnaire.dates.title": "Travel dates? 📅",
      "questionnaire.dates.description": "Fixed or flexible dates",
      "questionnaire.dates.fixed": "Fixed dates",
      "questionnaire.dates.flexible": "I'm flexible",
      "questionnaire.dates.selectDates": "Select dates 🗓️",
      "questionnaire.dates.selectDates.description": "Departure then return date",
      "questionnaire.dates.selectRange": "Click and drag to select a date range",
      "questionnaire.dates.selected": "Selected dates:",
      "questionnaire.dates.departure": "Departure:",
      "questionnaire.dates.return": "Return:",
      "questionnaire.dates.nights": "nights",
      "questionnaire.dates.reset": "Reset dates",
      "questionnaire.dates.pickDeparture": "Select your departure date",
      "questionnaire.dates.pickReturn": "Now select your return date",
      "questionnaire.dates.selectedMsg": "Dates selected!",

      // Flexibility
      "questionnaire.flexibility.title": "Date flexibility? ⏰",
      "questionnaire.flexibility.description": "Possible date shift for better prices",
      "questionnaire.flexibility.souplesse": "What flexibility? 🔄",
      "questionnaire.flexibility.0days": "±1d",
      "questionnaire.flexibility.3days": "±3d",
      "questionnaire.flexibility.7days": "±7d",
      "questionnaire.flexibility.14days": "±14d",
      "questionnaire.flexibility.30days": "±30d",
      "questionnaire.flexibility.week": "±1 week",
      "questionnaire.flexibility.2weeks": "±2 weeks",
      "questionnaire.flexibility.month": "±1 month",
      "questionnaire.flexibility.hasApproxDate": "Approximate departure? 📅",
      "questionnaire.flexibility.hasApproxDate.description": "Do you have a rough idea of when?",
      "questionnaire.flexibility.hasApproxDate.yes": "Yes, I have an idea",
      "questionnaire.flexibility.hasApproxDate.no": "Not yet",
      "questionnaire.flexibility.approxDate": "What is your approximate departure date? 📆",
      "questionnaire.flexibility.clickDate":
        "Click on a date (this date will serve as a reference for your flexibility)",
      "questionnaire.flexibility.dateSelected": "Date selected:",
      "questionnaire.dates.clickInstruction": "Click on your departure date then on your return date ✈️",
      "questionnaire.dates.duration": "Trip duration:",
      "questionnaire.dates.days": "days",

      // Duration
      "questionnaire.duration.title": "Trip duration? 🌙",
      "questionnaire.duration.description": "Number of nights",
      "questionnaire.duration.2nights": "2 nights",
      "questionnaire.duration.3nights": "3 nights",
      "questionnaire.duration.4nights": "4 nights",
      "questionnaire.duration.5nights": "5 nights",
      "questionnaire.duration.6nights": "6 nights",
      "questionnaire.duration.7nights": "7 nights",
      "questionnaire.duration.8to10": "8-10 nights",
      "questionnaire.duration.11to14": "11-14 nights",
      "questionnaire.duration.more14": ">14 nights",
      "questionnaire.duration.exactNights": "Exact number of nights? 🌉",
      "questionnaire.duration.exactNights.description": "Minimum 15 nights",
      "questionnaire.duration.specify8to10": "Specify number of nights 🛏️",
      "questionnaire.duration.specify8to10.description": "Choose between 8, 9 or 10 nights",
      "questionnaire.duration.specify11to14": "Specify number of nights 🛏️",
      "questionnaire.duration.specify11to14.description": "Choose between 11, 12, 13 or 14 nights",
      "questionnaire.duration.8nights": "8 nights",
      "questionnaire.duration.9nights": "9 nights",
      "questionnaire.duration.10nights": "10 nights",
      "questionnaire.duration.11nights": "11 nights",
      "questionnaire.duration.12nights": "12 nights",
      "questionnaire.duration.13nights": "13 nights",
      "questionnaire.duration.14nights": "14 nights",

      // Budget
      "questionnaire.budget.title": "Budget per person? 💰",
      "questionnaire.budget.description": "Total budget excluding personal purchases",
      "questionnaire.budget.sliderDescription": "Move the slider to set your budget per person",
      "questionnaire.budget.perPerson": "per person",
      "questionnaire.budget.excluding": "Excluding personal purchases",
      "questionnaire.budget.less300": "<€300",
      "questionnaire.budget.300to600": "€300-600",
      "questionnaire.budget.600to900": "€600-900",
      "questionnaire.budget.900to1200": "€900-1,200",
      "questionnaire.budget.1200to1800": "€1,200-1,800",
      "questionnaire.budget.more1800": ">€1,800",
      "questionnaire.budget.dontKnow": "I don't know",
      "questionnaire.budget.precise": "Precise budget",
      "questionnaire.budget.totalBudget": "Exact budget? 💵",
      "questionnaire.budget.totalBudget.description": "Per person for the entire trip",
      "questionnaire.budget.entireTrip": "For the entire trip",
      "questionnaire.budget.amount": "Amount",
      "questionnaire.budget.currency": "Currency",
      "questionnaire.budget.enterPrecise": "Enter a precise budget",
      "questionnaire.budget.backToSlider": "Back to slider",

      // Travel styles
      "questionnaire.styles.title": "Activity interests? 🎨",
      "questionnaire.styles.description": "Select up to 5 options",
      "questionnaire.styles.selectUpTo5": "Select up to 5 styles",
      "questionnaire.styles.nature": "Nature",
      "questionnaire.styles.cultureMuseums": "Culture & museums",
      "questionnaire.styles.food": "Food",
      "questionnaire.styles.beach": "Beach",
      "questionnaire.styles.mountainHiking": "Mountain & hiking",
      "questionnaire.styles.photoSpots": "Photo spots",
      "questionnaire.styles.localMarkets": "Local life & markets",
      "questionnaire.styles.sportOutdoor": "Sport & outdoor",
      "questionnaire.styles.wellnessSpa": "Wellness & spa",
      "questionnaire.styles.nightlife": "Nightlife",

      // Rhythm
      "questionnaire.rhythm.title": "Trip pace? ⏱️",
      "questionnaire.rhythm.description": "Activities per day",
      "questionnaire.rhythm.selectRhythm": "Select your pace",
      "questionnaire.rhythm.cool": "Cool",
      "questionnaire.rhythm.cool.desc": "1-2 activities/day",
      "questionnaire.rhythm.relaxed": "Relaxed",
      "questionnaire.rhythm.relaxed.desc": "1-2 activities/day",
      "questionnaire.rhythm.balanced": "Balanced",
      "questionnaire.rhythm.balanced.desc": "2-3 activities/day",
      "questionnaire.rhythm.intense": "Intense",
      "questionnaire.rhythm.intense.desc": "3+ activities/day",

      // Schedule preferences
      "questionnaire.optional": "Optional",
      "questionnaire.schedule.title": "Schedule preferences",
      "questionnaire.schedule.description": "Select up to 3 options",
      "questionnaire.schedule.maxReached": "Maximum 3 preferences",
      "questionnaire.schedule.earlyBird": "Early bird",
      "questionnaire.schedule.nightOwl": "Night owl",
      "questionnaire.schedule.needsSiesta": "Needs siesta",
      "questionnaire.schedule.needsBreaks": "Needs breaks",
      "questionnaire.schedule.needsFreeTime": "Needs free time",
      "questionnaire.schedule.flexibleSchedule": "Flexible schedule",

      // Flights
      "questionnaire.flights.title": "Flight preference? ✈️",
      "questionnaire.flights.description": "Direct or with layovers",
      "questionnaire.flights.directOnly": "Direct only",
      "questionnaire.flights.max1Stop": "Max 1 stop",
      "questionnaire.flights.dontMind": "Don't mind",

      // Luggage
      "questionnaire.luggage.title": "Luggage per traveler? 🧳",
      "questionnaire.luggage.description": "Impacts flight price",
      "questionnaire.luggage.travelers": "traveler",
      "questionnaire.luggage.travelers_plural": "travelers",
      "questionnaire.luggage.traveler": "Traveler",
      "questionnaire.luggage.personalItem": "Personal item only",
      "questionnaire.luggage.cabin": "Cabin baggage only",
      "questionnaire.luggage.hold": "Checked baggage only",
      "questionnaire.luggage.cabinHold": "Cabin + Checked",

      // Accommodation type
      "questionnaire.accommodationType.title": "Accommodation type? 🏨",
      "questionnaire.accommodationType.description": "Select 1 or 2 types",
      "questionnaire.accommodationType.select1or2": "Select 1 or 2 types",
      "questionnaire.accommodationType.hotel": "Hotel",
      "questionnaire.accommodationType.apartment": "Apartment",
      "questionnaire.accommodationType.villa": "Villa",
      "questionnaire.accommodationType.chalet": "Chalet",
      "questionnaire.accommodationType.condominium": "Condominium",
      "questionnaire.accommodationType.hostel": "Hostel",
      "questionnaire.accommodationType.guesthouse": "Guesthouse/riad",
      "questionnaire.accommodationType.lodge": "Lodge/eco-stay",
      "questionnaire.accommodationType.camping": "Camping/glamping",
      "questionnaire.accommodationType.bedBreakfast": "Bed & breakfast",
      "questionnaire.accommodationType.resort": "Resort",
      "questionnaire.accommodationType.dontMind": "Don't mind",

      // Hotel preferences
      "questionnaire.hotelPreferences.title": "Hotel preferences? 🏨",
      "questionnaire.hotelPreferences.description": "Services and amenities",
      "questionnaire.hotelPreferences.selectInterested": "Select what interests you",
      "questionnaire.hotelPreferences.breakfast": "Breakfast included",
      "questionnaire.hotelPreferences.halfBoard": "Half board",
      "questionnaire.hotelPreferences.fullBoard": "Full board",
      "questionnaire.hotelPreferences.allInclusive": "All-inclusive",
      "questionnaire.hotelPreferences.roomService": "Room service",
      "questionnaire.hotelPreferences.minibar": "Minibar",
      "questionnaire.hotelPreferences.view": "Sea/mountain view",
      "questionnaire.hotelPreferences.balcony": "Balcony/terrace",
      "questionnaire.hotelPreferences.dontMind": "Don't mind",
      "questionnaire.hotelPreferences.concierge": "Concierge service",

      // Comfort
      "questionnaire.comfort.title": "Minimum comfort? ⭐",
      "questionnaire.comfort.description": "Minimum rating required",
      "questionnaire.comfort.rating75": "Rating ≥7.5",
      "questionnaire.comfort.rating80": "Rating ≥8.0",
      "questionnaire.comfort.rating85": "Rating ≥8.5",
      "questionnaire.comfort.dontMind": "Don't mind",

      // Neighborhood
      "questionnaire.neighborhood.title": "Preferred neighborhood? 🗺️",
      "questionnaire.neighborhood.description": "Location type",
      "questionnaire.neighborhood.quiet": "Quiet",
      "questionnaire.neighborhood.centralLively": "Central & lively",
      "questionnaire.neighborhood.nearNatureBeach": "Near nature/beach",
      "questionnaire.neighborhood.atypicalCharm": "Atypical/local charm",

      // Amenities
      "questionnaire.amenities.title": "Desired amenities? 🛠️",
      "questionnaire.amenities.description": "Multiple choices possible",
      "questionnaire.amenities.selectAll": "Select all that matter to you",
      "questionnaire.amenities.reliableWifi": "Reliable Wi-Fi",
      "questionnaire.amenities.airConditioning": "Air conditioning",
      "questionnaire.amenities.kitchen": "Kitchen",
      "questionnaire.amenities.washingMachine": "Washing machine",
      "questionnaire.amenities.parking": "Parking",
      "questionnaire.amenities.elevator": "Elevator",
      "questionnaire.amenities.reception24": "24/7 reception",
      "questionnaire.amenities.nearWorship": "Near place of worship",
      "questionnaire.amenities.babyCrib": "Baby crib",
      "questionnaire.amenities.familyRoom": "Family room",
      "questionnaire.amenities.pool": "Pool",
      "questionnaire.amenities.gym": "Gym",
      "questionnaire.amenities.spa": "Spa/jacuzzi",
      "questionnaire.amenities.gardenTerrace": "Garden/terrace",
      "questionnaire.amenities.dontMind": "Don't mind",

      // Constraints
      "questionnaire.constraints.title": "Any constraints? 🎯",
      "questionnaire.constraints.description": "Dietary, religious, accessibility",
      "questionnaire.constraints.selectAllImportant": "Select all important options",
      "questionnaire.constraints.halal": "Halal",
      "questionnaire.constraints.kosher": "Kosher",
      "questionnaire.constraints.vegetarian": "Vegetarian",
      "questionnaire.constraints.vegan": "Vegan",
      "questionnaire.constraints.glutenFree": "Gluten-free",
      "questionnaire.constraints.noPork": "No pork",
      "questionnaire.constraints.noAlcohol": "No alcohol",
      "questionnaire.constraints.prayerPlaces": "Prayer places",
      "questionnaire.constraints.buddhistTraditions": "Respect Buddhist traditions",
      "questionnaire.constraints.accessibility": "Wheelchair accessibility",
      "questionnaire.constraints.safezones": "Safe zones",
      "questionnaire.constraints.avoidCar": "Avoid car",
      "questionnaire.constraints.localTraditions": "Respect local traditions",
      "questionnaire.constraints.foodAllergies": "Food allergies",
      "questionnaire.constraints.dontMind": "Don't mind",

      // Additional info
      "questionnaire.additionalInfo.title": "Additional info? 💬",
      "questionnaire.additionalInfo.description": "Important details we should know (optional)",
      "questionnaire.additionalInfo.descriptionExtended": "Ex: avoid stopovers, allergies, special event",
      "questionnaire.additionalInfo.optional": "Free text (optional)",
      "questionnaire.additionalInfo.placeholder": "Share your specific needs...",
      "questionnaire.additionalInfo.skip": "Skip",
      "questionnaire.additionalInfo.characters": "characters",

      // Email step
      "questionnaire.email.title": "Receive my itinerary 📧",
      "questionnaire.email.description": "We will send you your personalized itinerary within 48h",
      "questionnaire.email.placeholder": "your@email.com",
      "questionnaire.email.send": "Send 🚀",
      "questionnaire.email.sending": "Sending...",

      // Trip Details page
      "tripDetails.notFound": "Trip not found",
      "tripDetails.backToTrip": "Back to trip",
      "tripDetails.preBookingInfo": "Pre-booking information",
      "tripDetails.tripSummary": "Trip summary",
      "tripDetails.days": "days",
      "tripDetails.travelers": "travelers",
      "tripDetails.duration": "Duration",
      "tripDetails.travelersLabel": "Travelers",
      "tripDetails.activities": "Activities",
      "tripDetails.totalPrice": "Total price",
      "tripDetails.included": "Included in the trip",
      "tripDetails.excluded": "Not included",
      "tripDetails.responsibilities": "Responsibilities & Organization",
      "tripDetails.organizer": "Trip organizer",
      "tripDetails.organizerDesc": "Travliaq SAS acts as a travel organizer within the meaning of the European Directive 2015/2302. As such, we are responsible for the proper execution of all services included in your package.",
      "tripDetails.globalResponsibility": "Overall trip responsibility",
      "tripDetails.globalResponsibilityDesc": "In case of a problem with a provider (hotel, airline, etc.), you contact Travliaq directly. We handle claims and ensure coordination with all partners.",
      "tripDetails.cancellation": "Cancellation policy",
      "tripDetails.noRefundPolicy": "No refunds for cancellations",
      "tripDetails.noRefundDesc": "All bookings are final. No refunds will be issued for cancellations, regardless of the reason or timing before departure.",
      "tripDetails.insuranceRecommendation": "We strongly recommend purchasing travel cancellation insurance from your insurer to protect yourself against unforeseen circumstances.",
      "tripDetails.guarantees": "Guarantees & Protections",
      "tripDetails.insolvencyGuarantee": "Insolvency guarantee",
      "tripDetails.insolvencyGuaranteeDesc": "In accordance with European regulations, Travliaq has a financial guarantee that ensures reimbursement of amounts paid and repatriation of travelers in the event of company failure.",
      "tripDetails.rcProInsurance": "Professional Liability Insurance",
      "tripDetails.rcProInsuranceDesc": "Travliaq is covered by travel-specific professional liability insurance, covering damages that may occur during the organization of your trip.",
      "tripDetails.acceptTerms": "I have read and accept the",
      "tripDetails.termsLink": "Terms and Conditions",
      "tripDetails.acceptTerms2": "as well as the information regarding responsibilities and guarantees detailed above.",
      "tripDetails.proceedToBooking": "Proceed to booking",
      "tripDetails.footerNote": "In accordance with European Directive 2015/2302 on package travel, you benefit from all the rights provided for consumers.",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "fr",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  });

export default i18n;
